const emitFinderOutcome = (name, result_count) => document.dispatchEvent(new CustomEvent('cgau:commerce', { detail: { name, source: 'finder', result_count: Math.max(0, Math.min(1000, result_count)) } }));
const choice = (value, code, label, help) => ({ value, code, label, help });

const CATEGORY_QUESTION = {
  key: 'category',
  kicker: 'The scene',
  title: 'What are you protecting?',
  intro: 'Start with the job, not the model number.',
  why: 'This keeps the results relevant. A sensor, lock or indoor camera can never be used to fill an outdoor-camera result.',
  tip: 'Choose the place or task that matters most today. You can run the Match Desk again for another area.',
  options: [
    choice('outdoor', 'OUT', 'Outside the home', 'Driveway, yard, side access or shed'),
    choice('indoor', 'IN', 'Inside the home', 'Rooms, pets, entry points or a baby monitor'),
    choice('doorbell', 'DOOR', 'The front door', 'Visitors, parcels and entrance video'),
    choice('lock', 'LOCK', 'A door lock', 'Keypad, fingerprint, palm or video access'),
    choice('alarm', 'ALARM', 'An alarm system', 'Sensors, keypad, siren, fob or system hub'),
    choice('hub', 'HUB', 'A HomeBase hub', 'The base station eufy cameras and sensors pair with'),
    choice('accessory', 'EXTRA', 'Parts & accessories', 'Solar panels, mounts, storage, trackers, signs and add-ons'),
  ],
};

// v2 (21.10.2026, board seats): one tap per question, the open answer near the top, plain
// "I have ..." wording that separates owning a HomeBase from needing one, and a one-line
// explainer of what a HomeBase is.
const GENERAL_SYSTEM_QUESTION = {
  key: 'system',
  kicker: 'The HomeBase question',
  title: 'Do you already have a eufy HomeBase?',
  intro: 'A HomeBase is eufy’s hub: it stores footage and links devices. Many eufy cameras do not need one.',
  why: 'Products from the same brand can still use different hubs. We only pass an add-on when its recorded compatibility includes your HomeBase.',
  tip: 'No HomeBase yet? Choose “Not yet” and each result shows the HomeBase it needs, if any, with the full setup price.',
  options: [
    choice('any', 'OPEN', 'Not yet: show me what fits', 'Every result names the HomeBase it needs, if any, with the setup price'),
    choice('standalone', 'WI-FI', 'No, and I do not want one', 'Only products that work on their own over Wi-Fi'),
    choice('hb3', 'HB3', 'I have a HomeBase 3', 'Also sold as HomeBase S380'),
    choice('mini', 'MINI', 'I have a HomeBase Mini', 'The small hub that comes in eufyCam C35 and C37 kits'),
    choice('hb2', 'HB2', 'I have a HomeBase 2', 'Also sold as HomeBase S280'),
    choice('s1', 'S1', 'I have a HomeBase Professional S1', 'The professional hub with PoE camera ports'),
    choice('nvr', 'NVR', 'I have a eufy PoE recorder (NVR)', 'A wired recorder with Ethernet cameras'),
  ],
};

// v2 (21.10.2026): "I do not own a hub" meant "works with no hub", which no sensor does, so it
// counted "None fit" at step 2. It now shares the open answer: each device shows the HomeBase
// it needs, priced, and "Start the alarm system" still lists the HomeBases.
const ALARM_SYSTEM_QUESTION = {
  ...GENERAL_SYSTEM_QUESTION,
  title: 'Which alarm hub do you use?',
  intro: 'Sensors and controls are tied to a compatible HomeBase.',
  tip: 'No HomeBase yet? Choose “Not yet, or not sure” and each device shows the HomeBase it needs, with the full setup price.',
  options: [
    choice('any', 'OPEN', 'Not yet, or not sure', 'Each device shows the HomeBase it needs, with the setup price'),
    choice('hb3', 'HB3', 'I have a HomeBase 3', 'Also sold as HomeBase S380'),
    choice('mini', 'MINI', 'I have a HomeBase Mini', 'The small hub that comes in eufyCam C35 and C37 kits'),
    choice('hb2', 'HB2', 'I have a HomeBase 2', 'Also sold as HomeBase S280'),
    choice('s1', 'S1', 'I have a HomeBase Professional S1', 'The professional hub with PoE camera ports'),
  ],
};

const POWER_QUESTION = {
  key: 'power',
  kicker: 'The installation',
  title: 'How should it be powered?',
  intro: 'This changes both placement freedom and recording behaviour.',
  why: 'Your power choice filters the matches. If nothing in stock fits, anything shown with a different power source is labelled a close match, never a match.',
  tip: 'Continuous recording normally points toward direct power or PoE and may also need a recorder or storage upgrade. Solar and battery modes are usually event-based.',
  options: [
    choice('any', 'OPEN', 'No preference', 'Show every power option'),
    choice('wirefree', 'FREE', 'Battery or solar (no wires)', 'Rechargeable cameras; many can also take a solar panel'),
    choice('solar', 'SUN', 'Solar charging', 'Built-in, included or explicitly supported solar-panel operation'),
    choice('battery', 'BATT', 'Battery only', 'Rechargeable operation'),
    choice('plug', 'FIXED', 'Plugged in or hardwired', 'USB, plug-in or fixed wiring; check the adapter and installation needs'),
    choice('poe', 'POE', 'Power over Ethernet', 'One network cable for power and video data'),
  ],
};

// v2: detail and tracking now ORDER the results (a 2K camera says "2K detail", a fixed camera
// says "Fixed view"); only 24/7 recording still removes products, because an event camera
// genuinely cannot record around the clock. As filters, detail and tracking left 7 and 1 of
// 18 outdoor cameras (21.10.2026).
const PRIORITY_QUESTION = {
  key: 'priority',
  kicker: 'The deciding feature',
  title: 'What matters most to you?',
  intro: 'We list the best fit for this first. Only 24/7 recording is a must-have filter.',
  why: 'Detail and tracking reorder the results and each card says how it measures up. 24/7 recording removes cameras that cannot do it, because an event camera never records around the clock.',
  tip: 'Not sure? “Simplest setup” lists the products with the fewest extra parts first.',
  options: [
    choice('simple', 'EASY', 'Simplest setup', 'Fewest extra parts first'),
    choice('value', 'VALUE', 'Lowest price', 'Cheapest first; any HomeBase it needs is priced on the card'),
    choice('clarity', 'DETAIL', 'Sharpest picture', '4K and 3K cameras first; each card shows its resolution'),
    choice('coverage', 'TRACK', 'Follows movement', 'Pan-and-tilt and multi-lens cameras first; each card says if the view is fixed'),
    choice('continuous', '24/7', 'Records 24/7 (must-have)', 'Only cameras with verified 24/7 recording, plus what it needs'),
  ],
};

const ALARM_NEED_QUESTION = {
  key: 'alarmNeed',
  kicker: 'The alarm job',
  title: 'What do you need the alarm system to add?',
  intro: 'Each choice is a different device, not an interchangeable accessory.',
  why: 'An entry sensor cannot stand in for motion or water detection. This answer is always checked before recommending.',
  tip: 'The current E20 sensors, the Glass Break Sensor E10 and the E10 controls pair with HomeBase Mini, post-2021 HomeBase 2, HomeBase 3 or HomeBase Professional S1. Each product page lists its verified hubs and firmware.',
  options: [
    choice('entry', 'ENTRY', 'Door or window opening', 'An entry/contact sensor'),
    choice('motion', 'MOVE', 'Movement inside an area', 'A motion sensor'),
    choice('glass', 'GLASS', 'Breaking glass', 'A glass break sensor for windows and glass doors'),
    choice('flood', 'WATER', 'Leaks or freezing conditions', 'A flood and freeze sensor'),
    choice('control', 'CTRL', 'Arm, disarm or trigger SOS', 'A keypad or key fob'),
    choice('siren', 'SOUND', 'A louder indoor alert', 'An indoor siren'),
    choice('hub', 'BASE', 'Start the alarm system', 'The compatible HomeBase foundation first'),
  ],
};

const LOCK_NEED_QUESTION = {
  key: 'lockNeed',
  kicker: 'The doorway',
  title: 'What kind of lock job is this?',
  intro: 'A front-door deadbolt, an interior lever and a video lock solve different problems.',
  why: 'Door format and access method are treated as requirements when the catalogue data clearly identifies them.',
  tip: 'If you are replacing existing hardware, compare the product’s fitting diagram with your door before ordering.',
  options: [
    choice('front', 'ENTRY', 'Main entry / deadbolt', 'A conventional exterior entry door'),
    choice('interior', 'ROOM', 'Interior lever door', 'Bedroom, office or internal access'),
    choice('video', 'VIDEO', 'Video and lock together', 'See the visitor as part of the lock'),
    choice('biometric', 'BIO', 'Biometric access', 'Fingerprint or palm access is essential'),
    choice('any', 'OPEN', 'Help me compare', 'No special lock format selected'),
  ],
};

const ACCESSORY_NEED_QUESTION = {
  key: 'accessoryNeed',
  kicker: 'The add-on job',
  title: 'What should the add-on do?',
  intro: 'Genuine parts and add-ons, matched from the catalogue record rather than guessed.',
  why: 'A solar panel, a mount and a memory card solve different problems. This answer is checked before recommending.',
  tip: 'Open the compatibility details on each result: some add-ons fit specific models or connector types only.',
  options: [
    choice('power', 'SUN', 'Solar or charging power', 'Solar panels and charging accessories'),
    choice('mount', 'FIX', 'Mounting hardware', 'Pole, corner and angle mounts'),
    choice('storage', 'STORE', 'Storage or memory', 'Hard drives and microSD cards'),
    choice('tracker', 'FIND', 'Track an item', 'SmartTrack for keys, bags or luggage'),
    choice('display', 'VIEW', 'A viewing screen', 'Smart Display and its accessories'),
    choice('other', 'MORE', 'Another add-on', 'Anything else, from NVR parts to security signs'),
  ],
};

// v2: condition is no longer a question. It is a row of filter chips on the results (pick any
// mix, all on by default), so nobody answers it before seeing what exists. These labels feed
// the chips and the brief.
const CONDITION_QUESTION = {
  key: 'condition',
  kicker: 'Condition',
  title: 'Which conditions suit you?',
  intro: 'Pick any mix. Every card shows its exact grade.',
  why: 'Brand new is enforced exactly: a renewed or unsealed item is never shown as brand new.',
  tip: 'Every grade is a genuine product with our 12-month warranty.',
  options: [
    // v2: the same words as the grade on each card ("New No Box", "Renewed (Excellent)").
    choice('new', 'NEW', 'Brand New', 'Factory sealed'),
    choice('unboxed', 'OPEN', 'New No Box / Open Box', 'Unused: no box, open box or box damaged'),
    choice('renewed', 'RENEW', 'Renewed', 'Restored by the manufacturer, graded on each listing'),
    choice('value', 'SAVE', 'Renewed, open box or no box', 'Any condition other than brand new'),
    choice('any', 'BOTH', 'Show either', 'Every condition'),
  ],
};
const CONDITION_FILTERS = ['new', 'unboxed', 'renewed'];

const QUESTION_LIBRARY = [
  CATEGORY_QUESTION,
  GENERAL_SYSTEM_QUESTION,
  ALARM_SYSTEM_QUESTION,
  POWER_QUESTION,
  PRIORITY_QUESTION,
  ALARM_NEED_QUESTION,
  LOCK_NEED_QUESTION,
  ACCESSORY_NEED_QUESTION,
  CONDITION_QUESTION,
];

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
// v2 (21.10.2026): system, power and condition may hold several picks; a product passes when
// it fits ANY of them. A single string (older briefs, the harness) still works.
const picks = (answers, key) => asArray(answers?.[key]).filter(Boolean);
const lowerArray = (value) => asArray(value).map((item) => String(item).toLowerCase());
const productText = (product) => `${product.title || ''} ${asArray(product.tags).join(' ')}`.toLowerCase();
const hasAny = (values, patterns) => patterns.some((pattern) => values.some((value) => value.includes(pattern)));
const uniqueValues = (values) => [...new Set(asArray(values).filter(Boolean))];

const isWirelessS4 = (product) => {
  const identity = `${product.title || ''} ${product.modelFamily || ''}`.toLowerCase();
  return /\beufycam s4\s*\(wireless\)/.test(identity) &&
    !/(mount|bracket|case|cover|cable|adapter|replacement|spare)/.test(identity);
};

/*
 * Exact-model correction for eufyCam S4 (Wireless).
 *
 * The current Shopify record predates the finished finder schema: system_type is
 * blank, power_type only says Solar, and compatible_hubs includes Professional
 * S1. The official eufy product/support material instead documents standalone
 * Wi-Fi, HomeBase 3, solar/rechargeable/direct power, and conditional 24/7 use
 * over direct power with compatible microSD or hard-drive storage. HomeBase 3
 * is optional for that recording mode rather than a universal requirement.
 * Keeping this override exact-model-only prevents those capabilities leaking to
 * older eufyCam products or to S4 accessories while the catalogue is repaired.
 * Reviewed 2026-08-12 against eufy AU product T81721W1 and eufy Support's S4
 * introduction, storage, and continuous-recording guides.
 *
 * 21.10.2026: the hub list now comes from the record. The S4 x S1 conflict of
 * 12.08 was settled by the 25.09.2026 compatibility policy: the live checker's
 * T8172 row says HomeBase 3 or HomeBase Professional S1 (optional), never Mini
 * or HomeBase 2, and the cga.compatible_hubs value matches it. Forcing
 * HomeBase 3 only had hidden the S4 from every S1 brief.
 */
const effectiveCapabilities = (product) => {
  const capabilities = {
    systems: lowerArray(product.systems),
    hubs: lowerArray(product.hubs),
    power: lowerArray(product.power),
    recording: lowerArray(product.recording),
    design: lowerArray(product.design),
    resolution: lowerArray(product.resolution),
    storage: lowerArray(product.storage),
  };

  if (!isWirelessS4(product)) return capabilities;

  return {
    systems: uniqueValues([...capabilities.systems.filter((item) => item !== 'homebase required'), 'standalone']),
    hubs: capabilities.hubs.length ? capabilities.hubs : ['homebase 3', 'homebase professional s1'],
    power: uniqueValues([...capabilities.power, 'solar', 'battery', 'plug-in']),
    recording: uniqueValues([...capabilities.recording, 'event recording']),
    design: uniqueValues([...capabilities.design, 'pan and tilt', 'triple lens', 'bullet camera']),
    resolution: uniqueValues([...capabilities.resolution, '4k', '2k']),
    storage: uniqueValues([...capabilities.storage, '32gb built-in storage', 'microsd']),
  };
};

const knownPrice = (value) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const money = (cents, currency = 'AUD') => knownPrice(cents)
  ? new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100)
  : 'Price check needed';
const budgetCents = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return undefined;
  const [whole, fraction = ''] = text.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : undefined;
};

// v2: condition moved to the results (CONDITION_QUESTION), so camera paths are 4 taps, locks
// and alarms 3, accessories 2 and the HomeBase path 1.
const questionPath = (answers = {}) => {
  if (!answers.category) {
    return [CATEGORY_QUESTION, GENERAL_SYSTEM_QUESTION, POWER_QUESTION, PRIORITY_QUESTION];
  }

  // 13.08.2026: accessories skip the system question — a mount, memory card or tracker has
  // no hub relationship to gate on; each card still renders its recorded compatibility note.
  if (answers.category === 'accessory') {
    return [CATEGORY_QUESTION, ACCESSORY_NEED_QUESTION];
  }

  // 21.10.2026: a HomeBase was only reachable through "An alarm system", and never made the
  // six cards there. The hub path goes straight to results; each card points to the checker.
  if (answers.category === 'hub') {
    return [CATEGORY_QUESTION];
  }

  const questions = [
    CATEGORY_QUESTION,
    answers.category === 'alarm' ? ALARM_SYSTEM_QUESTION : GENERAL_SYSTEM_QUESTION,
  ];

  if (answers.category === 'alarm') questions.push(ALARM_NEED_QUESTION);
  else if (answers.category === 'lock') questions.push(LOCK_NEED_QUESTION);
  else questions.push(POWER_QUESTION, PRIORITY_QUESTION);

  return questions;
};

// The open answer for each question that has one. "Show my matches now" fills the questions
// still unanswered with these, so every default stays visible and editable in the brief.
const OPEN_DEFAULTS = { system: 'any', power: 'any', priority: 'simple', lockNeed: 'any', condition: 'any' };

const orList = (items) =>
  items.length < 2 ? items[0] || '' : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;

// Several picks read as "A, B or C".
const optionLabel = (key, value) => {
  if (Array.isArray(value)) return orList(value.map((one) => optionLabel(key, one)));
  for (const question of QUESTION_LIBRARY) {
    if (question.key !== key) continue;
    const option = question.options.find((item) => item.value === value);
    if (option) return option.label;
  }
  return value;
};

// v2 answers read "I have a HomeBase 3"; a sentence needs just the hub's name ("verified for
// HomeBase 3"). storefront-qa, 21.10.2026: four sentences printed "Your I have a HomeBase 3".
const hubLabel = (value) => {
  if (Array.isArray(value)) return orList(value.map(hubLabel));
  return String(optionLabel('system', value)).replace(/^I have an? /, '');
};

// 21.10.2026: a HomeBase is identified by its own record (system_type "Hub") or its title,
// never by tags. Camera tags such as "Compatible with HomeBase 3" put eight cameras into the
// alarm-hub results, and HomeBase 3's "IP & Smart Security Camera Systems" tag made it an
// outdoor camera. Titles that name another device ("... for HomeBase") are never hubs.
const isHubProduct = (product) => {
  const title = String(product.title || '').toLowerCase().replace(/\bfor\b.*$/, '');
  if (/(camera|\bcam\b|solocam|eufycam|doorbell|lock|sensor|keypad|siren|hard drive|display|kit\b)/.test(title)) return false;
  return lowerArray(product.systems).includes('hub') || /\bhomebase (2|3|mini|professional s1)\b/.test(title);
};

const categoryMatches = (product, category) => {
  const text = productText(product);
  const design = lowerArray(product.design);
  // 13.08.2026: an accessory word in a CAMERA title must not hide the camera — "eufyCam C37
  // … with Solar Panel" and "SoloCam S340 (3W Solar Panel)" were invisible in every flow.
  // The camera-word check runs on the TITLE only: accessory tags like "Security Camera
  // Brackets & Mounts" would otherwise make a plain solar panel read as a camera.
  const mainProductTitle = /(camera|\bcam\b|solocam|eufycam|doorbell|familock|smart lock)/.test(
    String(product.title || '').toLowerCase().replace(/\bfor\b.*$/, '')
  );
  const accessoryOnly =
    /(mount|wedge|cable|adapter|hard drive|memory card|power bank|replacement battery|solar panel)/.test(text) &&
    !mainProductTitle;

  if (category === 'outdoor') {
    const isOutdoorCamera =
      /(solocam|eufycam|wired cam|floodlight cam|wall light cam|outdoor security camera|nvr poe .*camera|network video recorder|poe (bullet|turret)|camera system)/.test(
        text
      ) || hasAny(design, ['floodlight camera', 'bullet camera', 'turret camera', 'triple lens']);
    return isOutdoorCamera && !/(indoor cam|doorbell|baby monitor|smart lock|familock)/.test(text) && !accessoryOnly && !isHubProduct(product);
  }
  if (category === 'indoor') {
    return /(indoor cam|baby monitor|playroom bundle)/.test(text) && !accessoryOnly;
  }
  if (category === 'doorbell') {
    return /doorbell/.test(text) && !/(mount|wedge|chime|transformer)/.test(text);
  }
  if (category === 'lock') {
    return /(smart lock|familock)/.test(text) && !/(replacement|battery pack|rechargeable battery)/.test(text);
  }
  if (category === 'alarm') {
    return /(entry sensor|motion sensor|glass break sensor|flood and freeze sensor|indoor siren|key fob|panic button|security keypad)/.test(
      text
    ) || isHubProduct(product);
  }
  if (category === 'hub') return isHubProduct(product);
  if (category === 'accessory') {
    const title = String(product.title || '').toLowerCase();
    // A chime is always an accessory even though eufy titles it "Video Doorbell Chime";
    // and "Bracket For E340 Doorbell"-style titles name their host product after "for".
    if (/chime/.test(title)) return true;
    const scopedTitle = title.replace(/\bfor\b.*$/, '');
    const isMainProduct =
      /(camera|\bcam\b|solocam|eufycam|doorbell|familock|smart lock|sensor|siren|keypad|key fob|panic button|baby monitor)/.test(
        scopedTitle
      );
    return (
      !isMainProduct &&
      /(solar panel|mount|bracket|wedge|cable|adapter|hard drive|memory card|micro ?sd|power bank|replacement battery|smarttrack|smart display|charging dock|charging stand|yard sign)/.test(
        text
      )
    );
  }
  return false;
};

// 13.08.2026: the catalogue writes PoE add-ons as "Wired PoE - NVR required" while older
// records use "PoE / NVR" — both must count, or the E40/E41 add-ons vanish from the NVR flow.
const isPoeNvrSystem = (systems) => hasAny(systems, ['poe / nvr', 'nvr required']);

const uncertainAddOn = (product) => {
  const text = productText(product);
  const { hubs, systems } = effectiveCapabilities(product);
  // An add-on with a clearly stated requirement is not "uncertain" — the Baby Monitor E21
  // add-on camera names its parent monitor and must stay visible on open briefs.
  return /add-on/.test(text) && !hubs.length && !isPoeNvrSystem(systems) && !product.requirement;
};

// v2: several connection picks ("I have a HomeBase 3 and a Mini") pass a product that fits any.
const systemMatches = (product, answers) =>
  picks(answers, 'system').some((system) => systemMatchesOne(product, { ...answers, system }));

const systemMatchesOne = (product, answers) => {
  const selected = answers.system;
  if (!selected) return false;
  const { systems, hubs } = effectiveCapabilities(product);

  if (selected === 'any') return !uncertainAddOn(product);
  if (selected === 'standalone') {
    if (answers.category === 'alarm' && answers.alarmNeed === 'hub') return isHubProduct(product);
    if (answers.category === 'alarm') return false;
    // 13.08.2026: addon_requirement also stores reassurances ("No hub required - works on its
    // own") and complete-kit notes ("No separate hub purchase required"); only a real
    // extra-part requirement may disqualify a standalone brief.
    // 21.10.2026: only a hub or recorder requirement can block it. "HomeBase optional; supply
    // microSD ..." (C35 White Renewed) and "... an optional C10 screen; microSD card ..." (C10
    // add-on camera) both rejected products recorded as standalone. Also flagged 22.09.2026.
    const requirementText = String(product.requirement || '').toLowerCase();
    const blockingRequirement =
      /(homebase|\bhub\b|\bnvr\b|recorder)/.test(requirementText) &&
      !/(no hub required|no separate hub|works on its own|optional|not required)/.test(requirementText);
    if (uncertainAddOn(product) || blockingRequirement) return false;
    // "Standalone - no HomeBase required" must not trip the hub-required exclusion — the
    // bare substring check silently removed all 26 products carrying that value.
    const requiresHomeBase = systems.some(
      (value) => value.includes('homebase required') && !value.includes('no homebase required')
    );
    if (requiresHomeBase || isPoeNvrSystem(systems)) return false;
    return hasAny(systems, ['standalone', 'complete kit']);
  }
  if (selected === 'nvr') return isPoeNvrSystem(systems);

  const hubNames = {
    hb2: 'homebase 2',
    hb3: 'homebase 3',
    mini: 'homebase mini',
    s1: 'homebase professional s1',
  };
  const target = hubNames[selected];
  if (!target) return false;
  if (hubs.includes(target)) return true;
  return false;
};

const powerMatches = (product, selected) => {
  const list = asArray(selected).filter(Boolean);
  if (!list.length || list.includes('any')) return true;
  const { power } = effectiveCapabilities(product);
  return list.some((one) => {
    if (one === 'wirefree') return hasAny(power, ['solar', 'battery']);
    if (one === 'solar') return hasAny(power, ['solar']);
    if (one === 'battery') return hasAny(power, ['battery']);
    if (one === 'plug') return hasAny(power, ['plug-in', 'hardwired']);
    if (one === 'poe') return hasAny(power, ['poe']);
    return false;
  });
};

const priorityMatches = (product, selectedOrAnswers) => {
  const answers =
    selectedOrAnswers && typeof selectedOrAnswers === 'object'
      ? selectedOrAnswers
      : { priority: selectedOrAnswers };
  const selected = answers.priority;
  // v2: detail and tracking rank (scoreProduct) instead of filtering; only 24/7 filters.
  if (!selected || ['simple', 'value', 'clarity', 'coverage'].includes(selected)) return true;
  const { recording } = effectiveCapabilities(product);
  if (selected === 'continuous') {
    // 24/7 needs a power pick other than solar or battery alone (several picks: any one will do).
    const powers = picks(answers, 'power');
    const systemsPicked = picks(answers, 'system');
    if (powers.length && powers.every((power) => ['solar', 'battery', 'wirefree'].includes(power))) return false;
    if (isWirelessS4(product)) {
      return systemsPicked.some((system) => ['standalone', 'hb3', 'any'].includes(system)) &&
        (!powers.length || powers.some((power) => ['plug', 'any'].includes(power)));
    }
    // S3 Pro's documented continuous route uses external power and HomeBase 3.
    if (product.mpn === 'T8162' && !systemsPicked.some((system) => ['hb3', 'any'].includes(system))) return false;
    return hasAny(recording, ['24/7 recording', 'continuous recording']);
  }
  return false;
};

// What a card says about the ranked wish, from the record (the real spec, never a score).
const wishFact = (product, answers) => {
  const { resolution, design } = effectiveCapabilities(product);
  if (answers.priority === 'clarity') {
    const best = ['16mp', '4k', '3k', '2k', '1080p'].find((value) => resolution.includes(value));
    if (!best) return 'Resolution not recorded';
    return ['16mp', '4k', '3k'].includes(best) ? `${best.toUpperCase()} detail` : `${best.toUpperCase()} detail, below 3K`;
  }
  if (answers.priority === 'coverage') {
    if (hasAny(design, ['pan and tilt'])) return 'Pans and tilts to follow movement';
    if (hasAny(design, ['triple lens', 'dual lens'])) return 'Extra lens for wider coverage';
    return 'Fixed view: no pan, tilt or extra lens';
  }
  return '';
};

const alarmNeedMatches = (product, selected) => {
  if (!selected) return false;
  const text = productText(product);
  if (selected === 'entry') return /entry sensor/.test(text);
  if (selected === 'motion') return /motion sensor/.test(text);
  if (selected === 'glass') return /glass break sensor/.test(text);
  if (selected === 'flood') return /flood and freeze sensor/.test(text);
  if (selected === 'control') return /(security keypad|key fob|panic button)/.test(text);
  if (selected === 'siren') return /indoor siren/.test(text);
  if (selected === 'hub') return isHubProduct(product);
  return false;
};

const lockNeedMatches = (product, selected) => {
  if (!selected || selected === 'any') return true;
  const text = productText(product);
  if (selected === 'interior') return /interior door|smart (lever )?lock c3[23]|familock c3[23]/.test(text);
  // 13.08.2026: the S3 ladder was titled two ways ('S3 Video Smart Lock' on the active
  // records, 'FamiLock S3 Smart Lock' on the drafts), so a video-lock brief silently
  // skipped half the ladder. Titles are now consistent; the matcher stays forgiving.
  if (selected === 'video') return /video smart lock|\bs3 max\b|\bs3 video\b|familock s3|\be330\b/.test(text);
  if (selected === 'biometric') return /fingerprint|palm vein/.test(text);
  if (selected === 'front') {
    return !/interior door|lever lock|smart lock c3[23]|familock c3[23]/.test(text);
  }
  return false;
};

const accessoryNeedMatches = (product, selected) => {
  if (!selected) return false;
  // Title only: collection-style tags ("Security Camera Brackets & Mounts" on a solar
  // panel) would otherwise file an accessory under the wrong job.
  const text = String(product.title || '').toLowerCase();
  const matchers = {
    power: /(solar panel|extension cable|charging cable|power cable|power bank)/,
    mount: /(mount|bracket|wedge)/,
    storage: /(hard drive|memory card|micro ?sd)/,
    tracker: /smarttrack/,
    display: /smart display/,
  };
  if (selected === 'other') {
    return Object.values(matchers).every((pattern) => !pattern.test(text));
  }
  const pattern = matchers[selected];
  return pattern ? pattern.test(text) : false;
};

// v2 condition picks: 'new' (sealed Brand New), 'unboxed' (new but open box, box damaged or no
// box), 'renewed' (Manufacturer Renewed, any grade). 'value' (= unboxed or renewed) and 'any'
// remain for older briefs.
const conditionMatches = (product, selected) => {
  const list = asArray(selected).filter(Boolean);
  if (!list.length || list.includes('any')) return true;
  const grade = String(product.condition || '').toLowerCase();
  return list.some((one) => {
    if (one === 'new') return grade === 'brand new';
    if (one === 'unboxed') return /open|no (?:retail )?box|box damaged/.test(grade) && !/renewed/.test(grade);
    if (one === 'renewed') return /renewed/.test(grade);
    if (one === 'value') return /renewed|open|no (?:retail )?box|box damaged/.test(grade);
    return false;
  });
};

const scoreProduct = (product, answers) => {
  let score = 100;
  const { resolution, design, systems } = effectiveCapabilities(product);

  if (answers.priority === 'clarity') {
    if (hasAny(resolution, ['16mp'])) score += 38;
    else if (hasAny(resolution, ['4k'])) score += 32;
    else if (hasAny(resolution, ['3k'])) score += 22;
  }
  if (answers.priority === 'coverage') {
    if (hasAny(design, ['triple lens'])) score += 28;
    if (hasAny(design, ['dual lens'])) score += 20;
    if (hasAny(design, ['pan and tilt'])) score += 16;
  }
  if (answers.priority === 'continuous') score += Math.min(18, resolution.length * 5);
  if (answers.priority === 'simple') {
    if (/(no hub required|no separate hub|works on its own)/i.test(String(product.requirement || ''))) score += 20;
    if (hasAny(systems, ['standalone'])) score += 12;
  }
  // Lowest product price is ordered directly in matchProducts; condition bonuses cannot override it.
  const conditionPicks = picks(answers, 'condition');
  const openCondition = !conditionPicks.length || conditionPicks.includes('any') || conditionPicks.length > 1;
  if (openCondition && String(product.condition || '').toLowerCase() === 'brand new') score += 5;
  if (picks(answers, 'system').some((system) => !['any', 'standalone'].includes(system))) score += 8;
  return score;
};

// The first connection and power picks this product actually fits (several may be picked).
const fitSystem = (product, answers) =>
  picks(answers, 'system').find((system) => systemMatchesOne(product, { ...answers, system }));
const fitPower = (product, answers) =>
  picks(answers, 'power').find((power) => power !== 'any' && powerMatches(product, power));

const matchReasons = (product, answers) => {
  const system = fitSystem(product, answers);
  const power = fitPower(product, answers);
  if (isWirelessS4(product)) {
    const s4Reasons = [];
    if (system === 'standalone') s4Reasons.push('Standalone Wi-Fi + local storage');
    else if (system === 'hb3') s4Reasons.push('Verified for HomeBase 3');
    else if (system === 's1') s4Reasons.push('Verified for Professional S1');
    else if (system === 'any') s4Reasons.push('Standalone, HomeBase 3 or S1');

    const s4Power = {
      solar: 'Solar charging',
      battery: 'Rechargeable wire-free use',
      plug: 'Direct-power capable',
    };
    if (s4Power[power]) s4Reasons.push(s4Power[power]);

    const s4Priority = {
      clarity: '4K fixed + dual 2K PTZ views',
      continuous: 'Conditional 24/7 support',
      coverage: 'Fixed + PTZ tracking views',
      simple: 'Complete standalone option',
      value: 'Exact fit ranked by price',
    };
    if (s4Priority[answers.priority]) s4Reasons.push(s4Priority[answers.priority]);
    if (product.condition) s4Reasons.push(product.condition);
    return [...new Set(s4Reasons)].slice(0, 4);
  }

  const reasons = [];
  const systemLabels = {
    hb2: 'Verified for HomeBase 2',
    hb3: 'Verified for HomeBase 3',
    mini: 'Verified for HomeBase Mini',
    s1: 'Verified for Professional S1',
    nvr: 'PoE / NVR fit',
  };
  if (answers.category === 'hub') reasons.push('HomeBase hub');
  if (system === 'standalone') {
    reasons.push(answers.category === 'alarm' ? 'System foundation' : 'No hub required');
  } else if (systemLabels[system]) reasons.push(systemLabels[system]);

  const powerLabels = {
    solar: 'Solar-capable',
    battery: 'Battery powered',
    plug: 'Fixed power',
    poe: 'Power over Ethernet',
  };
  if (powerLabels[power]) reasons.push(powerLabels[power]);

  const priorityLabels = {
    continuous: '24/7 recording',
    simple: 'Lower setup complexity',
    value: 'Ranked by product price',
  };
  const wish = wishFact(product, answers);
  if (wish) reasons.push(wish);
  else if (priorityLabels[answers.priority]) reasons.push(priorityLabels[answers.priority]);
  if (answers.alarmNeed) reasons.push(optionLabel('alarmNeed', answers.alarmNeed));
  if (answers.lockNeed && answers.lockNeed !== 'any') reasons.push(optionLabel('lockNeed', answers.lockNeed));
  if (answers.accessoryNeed) reasons.push(optionLabel('accessoryNeed', answers.accessoryNeed));
  if (product.condition) reasons.push(product.condition);
  return [...new Set(reasons)].slice(0, 4);
};

// Display names in one fixed order, so a card never lists hubs in record order.
const HUB_NAMES = [
  ['homebase 3', 'HomeBase 3'],
  ['homebase mini', 'HomeBase Mini'],
  ['homebase 2', 'HomeBase 2'],
  ['homebase professional s1', 'HomeBase Professional S1'],
];
const listWith = (items, word) =>
  items.length < 2 ? items[0] || '' : `${items.slice(0, -1).join(', ')} ${word} ${items[items.length - 1]}`;
const needsHomeBase = (product) =>
  effectiveCapabilities(product).systems.some((value) => value.includes('homebase required') && !value.includes('no homebase required'));
const worksOnItsOwn = (product) =>
  hasAny(effectiveCapabilities(product).systems, ['standalone', 'complete kit']) ||
  /(no hub required|no separate hub|works on its own|homebase optional)/i.test(String(product.requirement || ''));

// One plain fact about what the product needs to work, taken from its own record. Hub names
// come from cga.compatible_hubs, which matched the live checker on 21.10.2026 (harness check B).
const setupFact = (product, answers = {}) => {
  if (isHubProduct(product)) {
    return answers.category === 'alarm'
      ? 'It is a HomeBase, the hub alarm sensors and controls pair with.'
      : 'It is a HomeBase. Our compatibility checker lists every camera and sensor each HomeBase runs.';
  }
  const hubs = HUB_NAMES.filter(([key]) => effectiveCapabilities(product).hubs.includes(key)).map(([, name]) => name);
  const firmware = /firmware|\bv\d/i.test(String(product.compatibilityNote || '')) ? ' Your HomeBase may need a software update first; see Compatibility details.' : '';
  if (needsHomeBase(product) && hubs.length) return `It needs a HomeBase to work: ${listWith(hubs, 'or')}.${firmware}`;
  if (isPoeNvrSystem(effectiveCapabilities(product).systems)) return 'It needs a recorder to work; the requirement is shown below.';
  if (worksOnItsOwn(product)) {
    return hubs.length ? `It works on its own. Optional hub: ${listWith(hubs, 'or')}.` : 'It works on its own; no HomeBase is needed.';
  }
  return '';
};

const POWER_FACT = {
  solar: 'It can charge from solar.',
  battery: 'It runs on its rechargeable battery.',
  wirefree: 'It runs on battery or solar, with no power cable.',
  plug: 'It runs plugged in or hardwired.',
  poe: 'It runs on Power over Ethernet.',
};

const matchGuidance = (product, answers) => {
  const system = fitSystem(product, answers);
  const power = fitPower(product, answers);
  if (isWirelessS4(product)) {
    const why = [];
    if (system === 'standalone') {
      why.push('It can operate over direct Wi-Fi with 32GB built-in storage, so a HomeBase is not required for basic use.');
    } else if (system === 'hb3') {
      why.push('HomeBase 3 is a verified hub for this camera; HomeBase Professional S1 also pairs.');
    } else if (system === 's1') {
      why.push('HomeBase Professional S1 is a verified hub for this camera; HomeBase 3 also pairs.');
    } else {
      why.push('It can start over direct Wi-Fi and can also pair with HomeBase 3 or HomeBase Professional S1.');
    }

    // v2: "integrated 5.5W solar panel" dropped; the 22.09.2026 review read eufy's AU S4 page as
    // a detachable panel, and neither wording is verified here, so the line states only the fit.
    const s4Fit = fitPower(product, answers);
    if (s4Fit === 'solar') {
      why.push('It supports solar charging.');
    } else if (s4Fit === 'battery') {
      why.push('Its rechargeable battery supports wire-free placement.');
    } else if (s4Fit === 'plug') {
      why.push('It supports direct plug-in power.');
    }

    if (answers.priority === 'clarity') {
      why.push('The fixed overview lens records in 4K.');
    } else if (answers.priority === 'coverage') {
      why.push('A fixed overview plus two pan-and-tilt views provides the requested moving, multi-lens coverage.');
    } else if (answers.priority === 'continuous') {
      why.push('With direct power and local storage it can record 24/7; a HomeBase 3 is optional.');
    } else if (answers.priority === 'simple') {
      why.push('Basic direct-Wi-Fi use does not require another hub.');
    } else if (answers.priority === 'value') {
      why.push('It clears every selected requirement; current price then determines where it ranks among those exact matches.');
    }

    let tradeoff =
      'A HomeBase 3 or HomeBase Professional S1 is optional for basic standalone use, but some advanced features and expanded storage depend on the exact hub, storage and firmware setup.';
    if (answers.priority === 'continuous') {
      tradeoff =
        'S4 24/7 recording is not available in battery or solar mode: it needs 5V/3A or stronger direct power, compatible microSD or hard-drive storage, and the required current firmware.';
    } else if (answers.priority === 'clarity') {
      tradeoff = 'Only the fixed overview lens is 4K; the two movable pan-and-tilt lenses are 2K, not three separate 4K views.';
    } else if (power === 'solar') {
      tradeoff =
        'Solar is charging support, not unlimited power. Available sun, weather and recording activity still affect the battery trend.';
    } else if (power === 'battery') {
      tradeoff = 'Battery runtime varies with activity, settings, temperature and network conditions; it is designed around event recording.';
    } else if (answers.priority === 'coverage') {
      tradeoff =
        'The extra lenses and tracking are a premium coverage feature; placement and motion settings still determine what the camera captures.';
    }

    return {
      why: why.join(' '),
      tradeoff,
      requirement:
        answers.priority === 'continuous'
          ? 'For S4 24/7 recording: use 5V/3A+ direct power, compatible microSD or hard-drive storage, and camera firmware V1.0.5.4 or later. If using HomeBase 3, it needs firmware V3.7.2.8 or later.'
          : '',
    };
  }

  const capabilities = effectiveCapabilities(product);
  const why = [];
  // 21.10.2026: "why" now states one fact from the product's own record (setupFact) instead of
  // "Its recorded component requirement is shown below. Its known setup requirement is shown
  // below.", which printed on every card of an "I do not know yet" + "simple" brief.
  const fact = setupFact(product, answers);
  if (answers.category === 'accessory') {
    why.push('It is the kind of add-on you picked.');
  } else if (system && !['standalone', 'any'].includes(system)) {
    why.push(`It works with your ${hubLabel(system)}.`);
  } else if (fact) {
    why.push(fact);
  } else if (system === 'standalone') {
    why.push('It works on its own, with no HomeBase.');
  }

  // v2: plain words; "Its catalogue record explicitly includes ..." read to the customer seat
  // like a database covering itself (21.10.2026).
  if (power) {
    why.push(POWER_FACT[power] || `It runs on ${optionLabel('power', power).toLowerCase()}.`);
  }
  if (answers.priority === 'continuous') {
    why.push(priorityMatches(product, answers) ? 'It can record 24/7.' : 'It can record 24/7 when it runs on mains power.');
  } else if (answers.priority === 'value') {
    why.push('It clears every selected requirement before current price is used for ranking.');
  }

  // No generic default: a trade-off is shown only when it is specific to the brief.
  let tradeoff = '';
  if (answers.priority === 'continuous' && product.mpn === 'T8162') {
    // eufy AU S3 Pro page, read 21.10.2026: "To record 24/7, connect your eufyCam S3 Pro to an power
    // adapter and switch it to wired mode in the eufy App"; plug-in "(requires USB-C cable)".
    tradeoff = 'For 24/7 it needs a USB-C power adapter and cable, and Wired Mode switched on in the eufy app.';
  } else if (answers.priority === 'continuous') {
    tradeoff = '24/7 recording also needs steady power and somewhere to store the footage; the product page lists what it needs.';
  } else if (answers.priority === 'clarity') {
    tradeoff = 'Recorded resolution is only one part of usable detail; distance, lighting and placement still matter.';
  } else if (answers.priority === 'coverage') {
    tradeoff = 'Moving or multi-lens coverage still depends on placement, detection settings and the physical field of view.';
  } else if (power === 'solar') {
    tradeoff = 'Solar charging still depends on direct sun, weather and how often the camera wakes and records.';
  } else if (power === 'battery') {
    tradeoff = 'Battery runtime varies with activity, settings, temperature and network conditions.';
  } else if (!/brand new/i.test(String(product.condition || ''))) {
    tradeoff = 'Check the exact condition grade and what is in the box on the product page.';
  }

  return { why: why.join(' '), tradeoff, requirement: '' };
};

// openNeeds (live counts only): a device question not answered yet counts every device type.
// catalogue (when given): a budget is checked against the product plus the HomeBase or recorder
// it cannot work without, not the product price alone.
const evaluateProduct = (product, answers, { ignoreAvailability = false, openNeeds = false, catalogue = null } = {}) => {
  const failures = [];
  const needOpen = (key) => openNeeds && answers[key] === undefined;
  if (!ignoreAvailability && !product.available) failures.push('availability');
  if (!categoryMatches(product, answers.category)) failures.push('category');
  if (!['accessory', 'hub'].includes(answers.category) && !systemMatches(product, answers)) failures.push('system');
  if (!powerMatches(product, answers.power)) failures.push('power');
  if (!priorityMatches(product, answers)) failures.push('priority');
  if (answers.category === 'alarm' && !needOpen('alarmNeed') && !alarmNeedMatches(product, answers.alarmNeed)) failures.push('alarmNeed');
  if (answers.category === 'lock' && !lockNeedMatches(product, answers.lockNeed)) failures.push('lockNeed');
  if (answers.category === 'accessory' && !needOpen('accessoryNeed') && !accessoryNeedMatches(product, answers.accessoryNeed))
    failures.push('accessoryNeed');
  if (!conditionMatches(product, answers.condition)) failures.push('condition');
  if (answers.priority === 'value' && !knownPrice(product.price)) failures.push('price');
  if (knownPrice(answers.budgetCents)) {
    const cost = catalogue && knownPrice(product.price) ? setupFor(catalogue, product, answers).requiredTotal : product.price;
    if (!knownPrice(cost) || cost > answers.budgetCents) failures.push('budget');
  }

  return {
    passes: failures.length === 0,
    failures,
    product,
    score: scoreProduct(product, answers),
    reasons: matchReasons(product, answers),
    guidance: matchGuidance(product, answers),
  };
};

// Lowest price first: the "value" priority, and the two HomeBase routes, which have no feature
// question to rank on (calling the cheapest hub the "best exact fit" would overclaim).
const ranksByPrice = (answers) =>
  answers.priority === 'value' || answers.category === 'hub' || (answers.category === 'alarm' && answers.alarmNeed === 'hub');

const matchProducts = (products, answers, options = {}) => {
  const ranked = asArray(products)
    .map((product) => evaluateProduct(product, answers, { ...options, catalogue: asArray(products) }))
    .filter((item) => item.passes)
    .sort((a, b) => {
      const priceA = knownPrice(a.product.price) ? a.product.price : Infinity;
      const priceB = knownPrice(b.product.price) ? b.product.price : Infinity;
      const byPrice = priceA === priceB ? 0 : priceA < priceB ? -1 : 1;
      const byScore = b.score - a.score;
      return (ranksByPrice(answers) ? byPrice || byScore : byScore || byPrice)
        || String(a.product.id || a.product.handle || a.product.title).localeCompare(String(b.product.id || b.product.handle || b.product.title));
    });

  const unique = [];
  const seen = new Set();
  for (const item of ranked) {
    // Different condition offers are separate products and remain available to compare.
    const key = String(item.product.id || item.product.handle || item.product.title || '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
    if (unique.length === (options.limit || 3)) break;
  }
  return unique;
};

// ---- v2 (21.10.2026): live counts, close matches, setups ----

// Listings of one model (same mpn) sometimes record different power or recording options: on
// 21.10.2026 both in-stock S3 Pro listings said "Solar" and "Event recording" while their
// sold-out twin said "Battery, Solar, Plug-in" and "Continuous recording". A model can do
// what any of its listings records, so each listing gets the model's union for those two
// fields. Everything else stays per listing. (The record fixes are in the data manifest.)
const normaliseCatalogue = (products) => {
  const list = asArray(products);
  const byModel = new Map();
  for (const product of list) {
    if (!product?.mpn) continue;
    if (!byModel.has(product.mpn)) byModel.set(product.mpn, []);
    byModel.get(product.mpn).push(product);
  }
  return list.map((product) => {
    const siblings = product?.mpn ? byModel.get(product.mpn) : null;
    if (!siblings || siblings.length < 2) return product;
    const union = (key) => uniqueValues(siblings.flatMap((sibling) => asArray(sibling[key])));
    return { ...product, power: union('power'), recording: union('recording') };
  });
};

// Every unanswered question on the path takes its open answer, so a count says what the shopper
// would see if they stopped answering here.
const withOpenDefaults = (answers) => {
  const filled = { ...answers };
  for (const question of questionPath(answers)) {
    if (filled[question.key] === undefined && OPEN_DEFAULTS[question.key]) filled[question.key] = OPEN_DEFAULTS[question.key];
  }
  return filled;
};

// In-stock products that fit these answers (plus open answers for the rest).
const countInStock = (products, answers) => {
  const catalogue = asArray(products);
  const filled = withOpenDefaults(answers);
  return catalogue.filter((product) => product.available && evaluateProduct(product, filled, { openNeeds: true, catalogue }).passes).length;
};

// Answers that only rank or narrow, never safety or fit: one of these may be missed by a close
// match. Area, connection (hub), device type and lock format are never relaxed.
const SOFT_GATES = ['power', 'priority', 'condition', 'budget'];

const closeMatchLabel = (item, answers, catalogue = []) => {
  const product = item.product;
  const failure = item.failures[0];
  const capabilities = effectiveCapabilities(product);
  if (failure === 'power') {
    const has = asArray(product.power).join(' or ').toLowerCase() || 'not recorded';
    return `Power: ${has}, not ${optionLabel('power', picks(answers, 'power')).toLowerCase()}`;
  }
  if (failure === 'priority') {
    if (answers.priority === 'clarity') return `Detail: ${capabilities.resolution.map((r) => r.toUpperCase()).join(', ') || 'not recorded'}, below 3K`;
    if (answers.priority === 'continuous') return '24/7 recording needs mains power, not battery or solar';
    if (answers.priority === 'coverage') return 'Fixed view: no pan, tilt or extra lens';
  }
  if (failure === 'condition') return `Condition: ${product.condition || 'not recorded'}`;
  if (failure === 'budget') {
    const cost = catalogue.length ? setupFor(catalogue, product, answers).requiredTotal : product.price;
    return `Over your budget by ${money(cost - answers.budgetCents, product.currency)}${cost > product.price ? ' with the HomeBase it needs' : ''}`;
  }
  return 'Misses one of your answers';
};

// 24/7 is never relaxed (board, 21.10.2026). A 24/7 miss counts as close only when the product
// records 24/7 on mains power and the miss comes from a battery or solar power pick.
const continuousMissIsPowerOnly = (product, answers) =>
  answers.priority === 'continuous' && priorityMatches(product, { ...answers, power: 'plug' });

// In-stock products that miss exactly ONE soft answer, each labelled with what it misses.
// Someone who picked only Brand New is never offered renewed stock as a close match (a new
// product in an unsealed box can be).
const closeMatches = (products, answers, { limit = 3, exclude = [], excludeModels = [] } = {}) => {
  const catalogue = asArray(products);
  const skip = new Set(exclude.map(String));
  // storefront-qa, 21.10.2026: one model showed as an exact card AND a close card (C35 Green
  // exact, C35 White close), and repainting the exact card turned it into the close one.
  const skipModels = new Set(excludeModels.map(String));
  const newOnly = picks(answers, 'condition').length > 0 && picks(answers, 'condition').every((value) => value === 'new');
  return catalogue
    .filter((product) => product.available && !skip.has(String(product.id)) && !skipModels.has(modelKey(product)))
    .map((product) => evaluateProduct(product, answers, { catalogue }))
    .filter((item) => item.failures.length === 1 && SOFT_GATES.includes(item.failures[0]))
    .filter((item) => item.failures[0] !== 'priority' || continuousMissIsPowerOnly(item.product, answers))
    .filter((item) => !(item.failures[0] === 'condition' && newOnly && /renewed/i.test(String(item.product.condition || ''))))
    // A budget miss is "close" only within 20% of the budget (a $549 camera is not close to $150).
    .filter((item) => item.failures[0] !== 'budget' ||
      setupFor(catalogue, item.product, answers).requiredTotal <= answers.budgetCents * 1.2)
    .sort((a, b) => b.score - a.score || (a.product.price ?? Infinity) - (b.product.price ?? Infinity))
    .slice(0, limit)
    .map((item) => ({ ...item, miss: closeMatchLabel(item, answers, catalogue) }));
};

// The answers a close match's setup is built from, with its one named miss set aside: a camera
// shown for "24/7 needs mains power" gets the HomeBase its 24/7 recording needs, not "none in stock".
const relaxedAnswers = (answers, failure) => {
  if (failure === 'power') return { ...answers, power: 'any' };
  if (failure === 'priority' && answers.priority === 'continuous') return { ...answers, power: 'plug' };
  return answers;
};

// Which HomeBase a hub listing is, from its title.
const hubKey = (product) => {
  const title = String(product.title || '').toLowerCase();
  if (/homebase 3|\bs380\b/.test(title)) return 'homebase 3';
  if (/homebase mini/.test(title)) return 'homebase mini';
  if (/homebase 2|\bs280\b/.test(title)) return 'homebase 2';
  if (/professional s1|homebase s1/.test(title)) return 'homebase professional s1';
  return '';
};

const cardGb = (product) => Number((String(product.title || '').match(/(\d{2,4})\s?GB/i) || [])[1] || 0);

// The largest microSD card any listing of this model states it takes ("up to 256 GB", "32–256 GB").
const maxCardGb = (products, product) => {
  const sameModel = asArray(products).filter((p) => product.mpn && p.mpn === product.mpn);
  const texts = (sameModel.length ? sameModel : [product]).map((p) => `${p.compatibilityNote || ''} ${p.requirement || ''}`);
  const sizes = texts.flatMap((text) => [...text.matchAll(/(?:up to|to|-|–)\s?(\d{2,4})\s?GB/gi)].map((m) => Number(m[1])));
  return sizes.length ? Math.max(...sizes) : 0;
};

// A complete setup for one result: the product, plus the HomeBase it needs (when the shopper
// does not already have one) or a microSD card (when it records only to a card). Hubs come from
// cga.compatible_hubs (checker-aligned); a card only within the size the model's own record
// states. Every line is a separately sold, in-stock listing at its current price.
// How many products we sell (any stock) list this HomeBase: a plain, data-backed way to show
// which hub takes more cameras and sensors later.
// Counted by model (mpn), so one camera sold in four conditions counts once.
const hubReach = (products, key, { inStockOnly = false } = {}) =>
  new Set(
    asArray(products)
      .filter((p) => (!inStockOnly || p.available) && !isHubProduct(p) && effectiveCapabilities(p).hubs.includes(key))
      .map((p) => p.mpn || p.modelFamily || p.handle)
  ).size;

// The in-stock HomeBases a product pairs with, cheapest first. The shopper's condition picks
// are honoured when any hub meets them; when none does, a new hub in an unsealed box comes
// before a renewed one for a shopper who asked for new ("never a renewed hub inside a
// brand-new setup" unless there is no other, and then the card says so).
// A hub qualifies only if the product still meets EVERY answer with that hub: the S3 Pro pairs
// with a Mini, but its 24/7 recording needs a HomeBase 3 (board, 21.10.2026).
const HUB_ANSWER = { 'homebase 3': 'hb3', 'homebase mini': 'mini', 'homebase 2': 'hb2', 'homebase professional s1': 's1' };
const hubOptions = (products, product, answers = {}) => {
  const wanted = effectiveCapabilities(product).hubs;
  const fitsWith = (hub) => {
    const withHub = { ...answers, system: HUB_ANSWER[hubKey(hub)] };
    return systemMatchesOne(product, withHub) && priorityMatches(product, withHub);
  };
  const hubs = asArray(products)
    .filter((p) => p.available && knownPrice(p.price) && isHubProduct(p) && wanted.includes(hubKey(p)) && fitsWith(p))
    .sort((a, b) => a.price - b.price);
  const inCondition = hubs.filter((p) => conditionMatches(p, answers.condition));
  if (inCondition.length || !hubs.length) return { hubs: inCondition, conditionMet: true };
  const unsealed = hubs.filter((p) => conditionMatches(p, ['new', 'unboxed']));
  const wantsNew = picks(answers, 'condition').includes('new');
  return { hubs: wantsNew && unsealed.length ? unsealed : hubs, conditionMet: false };
};

const setupFor = (products, product, answers = {}) => {
  const lines = [{ role: 'item', product }];
  const inStock = asArray(products).filter((p) => p.available && knownPrice(p.price) && String(p.id) !== String(product.id));
  const ownsHub = picks(answers, 'system').some((system) => ['hb2', 'hb3', 'mini', 's1'].includes(system));
  let missing = '';
  let hubChoices = [];
  let hubConditionMet = true;
  if (needsHomeBase(product) && !ownsHub) {
    const { hubs, conditionMet } = hubOptions(products, product, answers);
    hubChoices = hubs;
    hubConditionMet = conditionMet;
    if (hubs.length) lines.push({ role: 'hub', product: hubs[0] });
    else missing = 'It needs a HomeBase, and no compatible HomeBase is in stock right now.';
  }
  // A PoE camera records only through a recorder (its requirement names which).
  if (isPoeNvrSystem(effectiveCapabilities(product).systems) && !picks(answers, 'system').includes('nvr') && !ownsHub) {
    const recorders = inStock.filter((p) => isHubProduct(p) && effectiveCapabilities(product).hubs.includes(hubKey(p)));
    if (recorders.length) lines.push({ role: 'hub', product: recorders[0] });
    else missing = `It needs a recorder${product.requirement ? ` (${String(product.requirement).replace(/\s*required\.?$/i, '')})` : ''}, and none is in stock right now.`;
  }
  const storage = effectiveCapabilities(product).storage;
  const recordsToCard = hasAny(storage, ['microsd']) && !hasAny(storage, ['built-in']);
  // Someone who already has a HomeBase records to it, so no card is offered to them.
  if (!ownsHub && !lines.some((line) => line.role === 'hub') && recordsToCard && !isHubProduct(product)) {
    const max = maxCardGb(products, product);
    const cards = inStock
      .filter((p) => /micro ?sd/i.test(p.title) && cardGb(p) && cardGb(p) <= max)
      .sort((a, b) => cardGb(b) - cardGb(a) || a.price - b.price);
    if (cards.length) lines.push({ role: 'card', product: cards[0] });
  }
  const total = lines.reduce((sum, line) => sum + line.product.price, 0);
  // What the product cannot work without (hub or recorder), for budgets: a $139 camera that
  // needs a $218 HomeBase is never shown under "$150".
  const requiredTotal = lines.filter((line) => line.role !== 'card').reduce((sum, line) => sum + line.product.price, 0);
  return { lines, total, requiredTotal, missing, hubChoices, hubConditionMet };
};

const alarmProductName = (selected) =>
  ({
    entry: 'entry sensor',
    motion: 'motion sensor',
    glass: 'glass break sensor',
    flood: 'flood and freeze sensor',
    control: 'keypad, key fob or panic button',
    siren: 'indoor siren',
    hub: 'alarm-system hub',
  })[selected] || 'alarm accessory';

const accessoryProductName = (selected) =>
  ({
    power: 'solar or charging accessory',
    mount: 'mounting accessory',
    storage: 'storage or memory accessory',
    tracker: 'item tracker',
    display: 'smart display or display accessory',
    other: 'accessory or add-on',
  })[selected] || 'accessory';

const needSentence = (answers) => {
  const category = {
    outdoor: 'outdoor camera',
    indoor: 'indoor camera or monitor',
    doorbell: 'video doorbell',
    lock: 'smart lock',
    alarm: alarmProductName(answers.alarmNeed),
    hub: 'HomeBase hub',
    accessory: accessoryProductName(answers.accessoryNeed),
  }[answers.category];
  // v2: several picks read as one phrase ("verified for HomeBase 3 or HomeBase Mini").
  const systemPicks = picks(answers, 'system').filter((value) => value !== 'any');
  const hubPicks = systemPicks.filter((value) => ['hb2', 'hb3', 'mini', 's1'].includes(value));
  const systemParts = [];
  if (systemPicks.includes('standalone')) {
    systemParts.push(answers.category === 'alarm' ? 'for a new system with no existing hub' : 'that can operate without a HomeBase or recorder');
  }
  if (hubPicks.length) systemParts.push(`verified for ${hubLabel(hubPicks)}`);
  if (systemPicks.includes('nvr')) systemParts.push('for a PoE NVR');
  const system = systemParts.join(', or ');
  const powerPicks = picks(answers, 'power').filter((value) => value !== 'any');
  const power = powerPicks.length ? `with ${optionLabel('power', powerPicks).toLowerCase()}` : '';
  const priority = {
    clarity: 'and 3K-or-better detail',
    continuous: 'and verified 24/7 recording',
    coverage: 'and wide or moving coverage',
    simple: 'with the simplest setup',
    value: 'at the lowest product price',
  }[answers.priority];
  // v2: no condition picked means no condition words ("An in-stock outdoor camera ..."); the words
  // match the grade chips on the cards.
  const conditionWords = { new: 'Brand New', unboxed: 'New No Box or Open Box', renewed: 'Renewed', value: 'Renewed, Open Box or No Box' };
  const conditionPicks = picks(answers, 'condition');
  const condition = !conditionPicks.length || conditionPicks.includes('any')
    ? ''
    : orList(conditionPicks.map((value) => conditionWords[value]).filter(Boolean));
  return `An in-stock ${[condition, category, system, power, priority].filter(Boolean).join(' ')}.`.replace(/\s+/g, ' ');
};

const naturalList = (items) => {
  const values = uniqueValues(items);
  if (values.length < 2) return values[0] || '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
};

const closestFailureSummary = (products, answers) => {
  const candidates = asArray(products)
    .filter((product) => product.available && categoryMatches(product, answers.category))
    .map((product) => evaluateProduct(product, answers))
    .map((item) => ({ ...item, relevantFailures: item.failures.filter((failure) => !['availability', 'category'].includes(failure)) }))
    .filter((item) => item.relevantFailures.length)
    .sort((a, b) => a.relevantFailures.length - b.relevantFailures.length || b.score - a.score);

  if (!candidates.length) return null;
  const minimum = candidates[0].relevantFailures.length;
  const closest = candidates.filter((item) => item.relevantFailures.length === minimum).slice(0, 6);
  const failures = uniqueValues(closest.flatMap((item) => item.relevantFailures));
  const labels = failures.map((failure) => {
    if (failure === 'system') {
      const owned = picks(answers, 'system').filter((value) => ['hb2', 'hb3', 'mini', 's1'].includes(value));
      if (owned.length) return `the ${hubLabel(owned)} pairing requirement`;
      if (picks(answers, 'system').includes('nvr')) return 'the PoE recorder requirement';
      return 'the no-HomeBase requirement';
    }
    if (failure === 'power') return `the ${optionLabel('power', answers.power)} power requirement`;
    if (failure === 'priority') return `the ${optionLabel('priority', answers.priority)} feature requirement`;
    if (failure === 'price') return 'a verified current product price';
    if (failure === 'budget') return 'the selected product-only budget';
    if (failure === 'condition') return `the ${optionLabel('condition', answers.condition)} condition requirement`;
    if (failure === 'alarmNeed') return `the ${optionLabel('alarmNeed', answers.alarmNeed)} device requirement`;
    if (failure === 'lockNeed') return `the ${optionLabel('lockNeed', answers.lockNeed)} lock-format requirement`;
    if (failure === 'accessoryNeed') return `the ${optionLabel('accessoryNeed', answers.accessoryNeed)} accessory requirement`;
    return failure;
  });
  return { failures, labels, candidateCount: closest.length };
};

const noMatchAdvice = (products, answers) => {
  const unavailable = matchProducts(products, answers, { ignoreAvailability: true, limit: 3 }).filter(
    (item) => !item.product.available
  );
  const closest = unavailable.length ? null : closestFailureSummary(products, answers);
  let headline = unavailable.length ? 'The right fit is sold out right now.' : 'We do not currently have an exact in-stock match.';
  let reason = unavailable.length
    ? 'At least one product we sell fits every answer, but it is sold out at the moment.'
    : closest
      ? `The closest in-stock products still miss ${naturalList(closest.labels)}, so we have not shown them as recommendations.`
      : 'Nothing we sell meets every part of this brief, so we have left the result empty instead of suggesting something unrelated.';
  let next = unavailable.length
    ? 'Open a model below to get a back-in-stock email, or ask us about restock timing.'
    : closest
      ? `Nothing in stock right now also meets ${naturalList(closest.labels)}. Change only an answer you can genuinely compromise on, or ask us about a specific model.`
      : 'Change only the answer you can genuinely compromise on, or ask us about a specific model.';

  // 21.10.2026: definite hub answers (25.09.2026 compatibility policy: unsettled means not
  // compatible, never "we cannot verify"). The data is cga.compatible_hubs, which the harness
  // compares with the live checker, across every product we sell including sold-out ones.
  const systemPicks = picks(answers, 'system');
  const hubSelected = systemPicks.length > 0 && systemPicks.every((system) => ['hb2', 'hb3', 'mini', 's1'].includes(system));
  const hub = hubLabel(systemPicks);
  const powerPicks = picks(answers, 'power');
  const eventPowerOnly = powerPicks.length > 0 && powerPicks.every((power) => ['solar', 'battery', 'wirefree'].includes(power));
  const eventPower = powerPicks.includes('wirefree') ? 'Battery or solar' : optionLabel('power', powerPicks);
  const [singular, plural] = {
    outdoor: ['outdoor camera', 'outdoor cameras'],
    indoor: ['indoor camera or monitor', 'indoor cameras and monitors'],
    doorbell: ['video doorbell', 'video doorbells'],
    lock: ['smart lock', 'smart locks'],
    alarm: [alarmProductName(answers.alarmNeed), {
      entry: 'entry sensors', motion: 'motion sensors', glass: 'glass break sensors', flood: 'flood and freeze sensors',
      control: 'keypads, key fobs and panic buttons', siren: 'indoor sirens',
    }[answers.alarmNeed]],
  }[answers.category] || [];
  const pairsWithHub = (product) =>
    categoryMatches(product, answers.category) &&
    (answers.category !== 'alarm' || alarmNeedMatches(product, answers.alarmNeed)) &&
    systemMatches(product, answers);
  const hubNeverPairs = hubSelected && Boolean(plural) && !asArray(products).some(pairsWithHub);

  // 13.08.2026: these special-case branches used to run unconditionally and overwrite the
  // sold-out headline/reason above, so a brief that DID match a real product (e.g. lock +
  // HomeBase 3 + video, which matches the S3 Max and E330) was told "we cannot verify"
  // while the panel simultaneously listed the matching product as sold out. When a matching
  // product exists and is merely unavailable, the honest sold-out message must stand.
  if (unavailable.length) {
    // keep the sold-out headline/reason/next computed above
  } else if (eventPowerOnly && answers.priority === 'continuous') {
    headline = `${eventPower} operation and true 24/7 recording do not overlap in the current range.`;
    reason =
      `CamGuard’s current cameras record events, not continuous 24/7 footage, when they run on ${eventPower.toLowerCase()} power. An event camera would not satisfy what you asked for.`;
    next = `For genuine 24/7 recording, choose a verified direct-power, fixed-power hardwired or PoE setup and meet its storage or hub requirements. If ${eventPower.toLowerCase()} power is non-negotiable, plan around event recording instead.`;
  } else if (answers.category === 'alarm' && systemPicks.length > 0 && systemPicks.every((system) => system === 'standalone') && answers.alarmNeed !== 'hub') {
    headline = 'This alarm accessory cannot work without a hub.';
    reason =
      'The selected alarm accessory is not a standalone product. It needs a compatible HomeBase, so selling the sensor by itself would leave you with an unusable setup.';
    next = 'Start with a compatible hub, then add the sensor or control. CamGuard’s current E20 sensors, Glass Break Sensor E10 and E10 controls are verified for HomeBase Mini, post-2021 HomeBase 2, HomeBase 3 and HomeBase Professional S1. Each product page lists its verified hubs and firmware minimums.';
  } else if (answers.category === 'alarm' && answers.alarmNeed === 'hub' && hubSelected) {
    headline = systemPicks.length === 1 ? `Your ${hub} is already the hub.` : 'You already have a HomeBase.';
    reason = 'Alarm sensors and controls pair straight to a HomeBase you already own, so you do not need a second one to start.';
    next = 'Go back one step and pick the sensor or control you want to add.';
  } else if (hubNeverPairs) {
    headline = `No ${singular} we sell pairs with ${hub}.`;
    reason = `We checked the records of all the ${plural} we sell, including sold-out ones, and none lists ${hub}.`;
    next =
      answers.category === 'lock'
        ? 'Every lock we sell works on its own over Wi-Fi. Change the HomeBase answer to “No, and I do not want one” to see the in-stock locks.'
        : answers.category === 'alarm'
          ? `If it does not have to join your ${hub}, change the HomeBase answer to “Not yet, or not sure”.`
          : `If it does not have to join your ${hub}, change the HomeBase answer to “No, and I do not want one” or “Not yet: show me what fits”.`;
  } else if (answers.category === 'lock' && hubSelected) {
    headline = `No in-stock lock fits every answer with ${hub}.`;
    reason = `The locks that pair with ${hub} do not match your other answers.`;
    next = 'Every lock we sell also works on its own over Wi-Fi. Change the HomeBase answer to “No, and I do not want one” to compare every in-stock lock, or edit another answer.';
  }

  return { headline, reason, next, need: needSentence(answers), unavailable };
};

const FinderBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
// Cards per page of results; "Show more matches" adds another page.
const RESULTS_PAGE = 6;
// A catalogue fetched when the last question opens is reused for results for this long.
const PREFETCH_MAX_AGE = 120000;
// v2: "Close, but not exact" appears when fewer models than this match every answer.
const CLOSE_MATCH_BELOW = 3;
// v2: setup-price chips on the results (cents).
const BUDGET_STEPS = [15000, 30000, 50000];

// v2: one card per model. Listings of the same model (mpn) share a card, with their grades
// and colours as options, so the C35 is one card instead of four.
const modelKey = (product) => String(product?.mpn || product?.modelFamily || product?.id || '');
const groupByModel = (items) => {
  const groups = [];
  const index = new Map();
  for (const item of items) {
    const key = modelKey(item.product);
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, items: [] });
    }
    groups[index.get(key)].items.push(item);
  }
  return groups;
};

// In-stock MODELS that fit these answers (open answers for the rest). Counting models, not
// listings: 18 outdoor listings were 11 cameras (board, 21.10.2026).
// inStockOnly: false counts sold-out models too, to tell "sold out" from "nothing we sell fits".
const countModels = (products, answers, { inStockOnly = true } = {}) => {
  const catalogue = asArray(products);
  const filled = withOpenDefaults(answers);
  return new Set(
    catalogue
      .filter((product) => (!inStockOnly || product.available) &&
        evaluateProduct(product, filled, { openNeeds: true, catalogue, ignoreAvailability: !inStockOnly }).passes)
      .map(modelKey)
  ).size;
};

const NOUNS = {
  outdoor: ['camera', 'cameras'],
  indoor: ['camera', 'cameras'],
  doorbell: ['doorbell', 'doorbells'],
  lock: ['lock', 'locks'],
  alarm: ['device', 'devices'],
  hub: ['HomeBase', 'HomeBases'],
  accessory: ['product', 'products'],
};
const nounFor = (category, count) => (NOUNS[category] || ['model', 'models'])[count === 1 ? 0 : 1];
// "18 in stock" read as 18 units left to the customer seat, so the line counts choices. A zero
// says "sold out" only when a matching model exists; "24/7" after "battery or solar" fits none.
const countLine = (count, category, listed = count) => {
  if (count) return `${count} ${nounFor(category, count)} to choose from`;
  return listed ? 'Sold out right now' : 'None fit your answers so far';
};

// In-stock count line per area for the first question (kept for the harness and older callers).
const categoryStockLine = (products, category) => {
  if (!products.length) return '';
  const count = countModels(products, { category });
  return countLine(count, category, count || countModels(products, { category }, { inStockOnly: false }));
};

// v2 quick starts (Gordon, 21.10.2026: "default options locked in ... on the most popular
// setups"). One tap fills the answers and shows results (or asks the one question it cannot
// guess). A quick start is hidden when it would show fewer than 3 models in stock.
const QUICK_STARTS = [
  {
    id: 'nohub',
    label: 'A camera that needs no hub',
    help: 'Works on its own over Wi-Fi',
    answers: { category: 'outdoor', system: 'standalone', power: 'any', priority: 'simple' },
  },
  {
    id: 'myhub',
    label: 'Add a camera to my HomeBase',
    help: 'Pick your HomeBase, then see what pairs with it',
    answers: { category: 'outdoor' },
    ask: 'system',
  },
  {
    id: 'wirefree',
    label: 'No-wiring outdoor cameras',
    help: 'Battery or solar powered',
    answers: { category: 'outdoor', system: 'any', power: 'wirefree', priority: 'simple' },
  },
  {
    id: 'indoor',
    label: 'An indoor camera',
    help: 'For pets, kids or the front room',
    answers: { category: 'indoor', system: 'any', power: 'any', priority: 'simple' },
  },
];
const MIN_QUICK_START_MODELS = 3;

const shortGrade = (condition) =>
  String(condition || 'Condition not recorded').replace(/^Manufacturer Renewed/i, 'Renewed').replace(/ Condition\)/i, ')');
const colourOf = (title) => (String(title || '').match(/\((Black|White|Green|Grey|Gray|Blue|Silver)\)/i) || [])[1] || '';
const modelName = (title) => String(title || '').split(' | ')[0].trim();
// A HomeBase line names the hub, not the brand: "HomeBase 3 S380" fits a phone-width setup box.
const hubName = (title) => modelName(title).replace(/^eufy\s+(security\s+)?/i, '');
const storeRoot = () => (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';

// v2 (21.10.2026): "Build my kit from this" hands the card's setup to the Bundle Builder at
// /pages/bundles (its contract, relayed by the parent session): ONE comma-separated with= of
// VARIANT ids (a Shopify preview redirect keeps only the last of repeated parameters), the
// shopper's hub, and from=match-desk. The builder re-checks price, stock and fit on arrival.
const BUILDER_HUB = { hb3: 'hb3', hb2: 'hb2', mini: 'mini', s1: 's1', standalone: 'new' };
const builderHub = (answers) => {
  const pick = picks(answers, 'system')[0];
  if (pick === 'any') return answers.category === 'alarm' ? 'unsure' : 'new';
  return BUILDER_HUB[pick] || '';
};
// No link unless every item in the setup has a variant id (a partial kit would mislead).
const builderHref = (items, answers) => {
  const ids = items.map((item) => String(item?.variantId ?? '')).filter((id) => /^\d+$/.test(id));
  if (!ids.length || ids.length !== items.length || ids.length > 10) return '';
  const hub = builderHub(answers);
  return `${storeRoot()}pages/bundles?with=${ids.join(',')}${hub ? `&hub=${hub}` : ''}&from=match-desk`;
};

class CgauCameraFinder extends FinderBase {
  constructor() {
    super();
    this.step = 0;
    this.answers = {};
    this.products = [];
    this.advanceTimer = null;
    this.resultVersion = 0;
    this.motion = new Set();
    this.visibleCount = RESULTS_PAGE;
    this.prefetch = null;
    this.choice = new Map(); // model key -> chosen listing id
    this.hubChoice = new Map(); // listing id -> chosen HomeBase listing id
    this.cardTicked = new Set(); // listing ids whose optional microSD card is ticked
    this.askThenResults = ''; // a quick start's one question, answered before results
    this.groupsByKey = new Map();
  }

  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.questionRoot = this.querySelector('[data-question]');
    this.progress = this.querySelector('[data-progress]');
    this.resultsRoot = this.querySelector('[data-results-root]');
    this.briefRoot = this.querySelector('[data-brief]');
    this.liveStatus = this.querySelector('[data-live-status]');
    this.resetButton = this.querySelector('.cgau-finder__reset-link');
    this.products = normaliseCatalogue(this.readCatalogue());
    this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
    this.stopMotion = () => { this.motion.forEach((animation) => animation.cancel()); this.motion.clear(); };
    this.onMotionVisibility = () => { if (document.hidden) this.stopMotion(); };
    this.motionPreference.addEventListener('change', this.stopMotion);
    document.addEventListener('visibilitychange', this.onMotionVisibility);
    if ('IntersectionObserver' in window) {
      this.motionObserver = new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) this.stopMotion(); });
      this.motionObserver.observe(this);
    }
    // v2: a slim bar that stays in reach on a phone: "Show my 9 cameras" from step 2 on.
    this.sticky = document.createElement('div');
    this.sticky.className = 'cgau-finder__sticky';
    this.sticky.hidden = true;
    this.sticky.innerHTML = '<button type="button" data-skip-to-results></button>';
    (this.querySelector('.cgau-finder__panel') || this).append(this.sticky);
    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('change', (event) => this.handleChange(event));
    this.renderQuestion();
  }

  disconnectedCallback() {
    window.clearTimeout(this.advanceTimer);
    this.catalogueAbort?.abort();
    this.prefetch?.controller.abort();
    this.prefetch = null;
    this.resultVersion++;
    this.stopMotion?.();
    this.motionObserver?.disconnect();
    this.motionPreference?.removeEventListener('change', this.stopMotion);
    document.removeEventListener('visibilitychange', this.onMotionVisibility);
    this.trayWatch?.disconnect();
    this.trayWatch = null;
    if (this.onTrayResize) window.removeEventListener('resize', this.onTrayResize);
  }

  settle(element, direction = 1, delay = 0) {
    if (!element?.animate || this.motionPreference.matches || document.hidden) return;
    const rect = element.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= innerHeight) return;
    const animation = element.animate([
      { transform: `translateX(${direction * 16}px)`, opacity: .65 },
      { transform: 'translateX(0)', opacity: 1 },
    ], { duration: 240, delay, fill: delay ? 'backwards' : 'none', easing: 'cubic-bezier(.22,1,.36,1)' });
    this.motion.add(animation);
    animation.finished.catch(() => {}).finally(() => this.motion.delete(animation));
  }

  readCatalogue() {
    try {
      const source = this.querySelector('[data-finder-catalog]');
      const payload = JSON.parse(source?.textContent || '{"products":[]}');
      this.catalogueMeta = payload;
      return asArray(payload.products);
    } catch (error) {
      console.warn('[CamGuard Match Desk] Could not read the catalogue.', error);
      return [];
    }
  }

  // The old budget field (if a theme still renders it). v2 sets the budget with chips.
  readBudget() {
    const input = this.querySelector('[data-product-budget]');
    if (!input) return true;
    const cents = budgetCents(input.value);
    input.setCustomValidity(cents === undefined ? 'Enter a positive amount with no more than two decimal places, or leave this blank.' : '');
    if (cents === undefined) { const details = input.closest('details'); if (details) details.open = true; }
    if (!input.reportValidity()) return false;
    this.answers.budgetCents = cents;
    return true;
  }

  async refreshCatalogue(signal) {
    let url = new URL(location.href);
    url.search = ''; url.hash = '';
    const sectionId = this.dataset.sectionId;
    if (!sectionId) throw new Error('Catalogue section unavailable');
    const products = [], seenPages = new Set();
    let expectedTotal = null;
    for (let page = 0; page < 100; page++) {
      url.searchParams.set('section_id', sectionId);
      if (seenPages.has(url.href)) throw new Error('Repeated catalogue page');
      seenPages.add(url.href);
      const response = await fetch(url, { signal, cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalogue request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = JSON.parse(doc.querySelector('[data-finder-catalog]')?.textContent || 'null');
      if (!payload || !Array.isArray(payload.products) || !Number.isInteger(payload.totalItems)) throw new Error('Incomplete catalogue response');
      if (expectedTotal !== null && expectedTotal !== payload.totalItems) throw new Error('Catalogue changed during refresh');
      expectedTotal = payload.totalItems;
      products.push(...payload.products);
      if (!payload.nextUrl) {
        if (products.length !== expectedTotal || new Set(products.map((p) => String(p.id))).size !== products.length) throw new Error('Catalogue count mismatch');
        return products;
      }
      const next = new URL(payload.nextUrl, url);
      if (next.origin !== location.origin || next.pathname !== url.pathname) throw new Error('Invalid catalogue continuation');
      url = next;
    }
    throw new Error('Catalogue pagination limit reached');
  }

  // 21.10.2026: start the stock refresh while the shopper reads the last question, so results
  // do not wait on a fresh 160 KB section fetch after the final tap. renderResults() reuses it
  // when it is under two minutes old, and fetches again if it failed.
  prefetchCatalogue() {
    if (this.prefetch && Date.now() - this.prefetch.at < PREFETCH_MAX_AGE) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    const promise = this.refreshCatalogue(controller.signal).finally(() => window.clearTimeout(timeout));
    promise.catch(() => {});
    this.prefetch = { at: Date.now(), promise, controller };
  }

  // Fill every unanswered question with its open answer and show results now.
  skipToResults() {
    if (!this.readBudget()) return;
    window.clearTimeout(this.advanceTimer);
    for (const question of questionPath(this.answers)) {
      if (!this.answers[question.key] && OPEN_DEFAULTS[question.key]) this.answers[question.key] = OPEN_DEFAULTS[question.key];
    }
    this.renderBrief();
    this.liveStatus.textContent = 'The remaining questions were set to their open answers. Showing matches.';
    void this.renderResults();
  }

  canSkip() {
    const questions = questionPath(this.answers);
    return this.step >= 1 && this.step < questions.length - 1 &&
      questions.every((question) => this.answers[question.key] || OPEN_DEFAULTS[question.key]);
  }

  startQuick(id) {
    const quick = QUICK_STARTS.find((item) => item.id === id);
    if (!quick) return;
    window.clearTimeout(this.advanceTimer);
    this.answers = { budgetCents: this.answers.budgetCents, ...quick.answers };
    this.choice.clear();
    this.renderBrief();
    if (quick.ask) {
      this.askThenResults = quick.ask;
      this.step = Math.max(0, questionPath(this.answers).findIndex((question) => question.key === quick.ask));
      this.renderQuestion({ focus: true });
      return;
    }
    this.askThenResults = '';
    this.skipToResults();
  }

  handleClick(event) {
    const target = event.target;
    if (target.closest('[data-apply-budget]')) {
      if (this.readBudget() && !this.resultsRoot.hidden) void this.renderResults();
      return;
    }
    const quick = target.closest('[data-quick-start]');
    if (quick) { this.startQuick(quick.dataset.quickStart); return; }
    if (target.closest('[data-skip-to-results]')) { this.skipToResults(); return; }
    if (target.closest('[data-show-more]')) { this.showMore(); return; }
    const condition = target.closest('[data-filter-condition]');
    if (condition) { this.toggleCondition(condition.dataset.filterCondition); return; }
    const budget = target.closest('[data-filter-budget]');
    if (budget) { this.setBudget(budget.dataset.filterBudget); return; }
    const option = target.closest('[data-model-option]');
    if (option) { this.chooseOption(option.dataset.modelKey, option.dataset.id); return; }
    const addOne = target.closest('[data-add-one]');
    if (addOne) { void this.addListings(addOne, [addOne.dataset.addOne]); return; }
    const addSetup = target.closest('[data-add-setup]');
    if (addSetup) { void this.addListings(addSetup, String(addSetup.dataset.addSetup).split(',')); return; }
    const choiceButton = target.closest('[data-choice]');
    if (choiceButton) { this.selectChoice(choiceButton); return; }
    const editButton = target.closest('[data-edit-key]');
    if (editButton) { this.editAnswer(editButton.dataset.editKey); return; }
    if (target.closest('[data-back]')) {
      window.clearTimeout(this.advanceTimer);
      this.step = Math.max(0, this.step - 1);
      this.renderQuestion({ focus: true, direction: -1 });
      return;
    }
    if (target.closest('[data-edit-answers]')) {
      this.resultsRoot.hidden = true;
      this.questionRoot.hidden = false;
      this.step = Math.max(0, questionPath(this.answers).length - 1);
      this.renderQuestion({ focus: true, direction: -1 });
      return;
    }
    if (target.closest('[data-restart]')) this.restart();
  }

  handleChange(event) {
    const hub = event.target.closest('[data-hub-select]');
    if (hub) { this.hubChoice.set(String(hub.dataset.id), String(hub.value)); this.repaintCard(hub); return; }
    const tick = event.target.closest('[data-card-tick]');
    if (tick) {
      const id = String(tick.dataset.id);
      if (tick.checked) this.cardTicked.add(id);
      else this.cardTicked.delete(id);
      this.repaintCard(tick);
    }
  }

  selectChoice(button) {
    if (!this.readBudget()) return;
    window.clearTimeout(this.advanceTimer);
    const questions = questionPath(this.answers);
    const question = questions[this.step];
    const previous = this.answers[question.key];

    if (question.key === 'category' && previous && previous !== button.dataset.choice) {
      this.answers = { budgetCents: this.answers.budgetCents };
      this.choice.clear();
    }
    if (question.key === 'category') this.askThenResults = '';
    this.answers[question.key] = button.dataset.choice;
    this.questionRoot.querySelectorAll('[data-choice]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === button));
      item.classList.toggle('is-selected', item === button);
    });
    this.renderBrief();
    this.settle(this.briefRoot.querySelector(`[data-edit-key="${question.key}"]`), 0);

    const finish = this.askThenResults === question.key;
    if (finish) this.askThenResults = '';
    const selectedLabel = optionLabel(question.key, button.dataset.choice);
    this.liveStatus.textContent = `${selectedLabel} selected. ${finish ? 'Showing matches.' : 'Moving to the next question.'}`;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.advanceTimer = window.setTimeout(() => (finish ? this.skipToResults() : this.advance()), reducedMotion ? 0 : 260);
  }

  advance() {
    const questions = questionPath(this.answers);
    if (this.step < questions.length - 1) {
      this.step += 1;
      this.renderQuestion({ focus: true });
    } else {
      void this.renderResults();
    }
  }

  editAnswer(key) {
    window.clearTimeout(this.advanceTimer);
    const questions = questionPath(this.answers);
    const index = questions.findIndex((question) => question.key === key);
    if (index < 0) return;
    this.resultsRoot.hidden = true;
    this.questionRoot.hidden = false;
    this.step = index;
    this.renderQuestion({ focus: true, direction: -1 });
  }

  restart() {
    window.clearTimeout(this.advanceTimer);
    this.step = 0;
    this.answers = {};
    this.choice.clear();
    this.hubChoice.clear();
    this.cardTicked.clear();
    this.askThenResults = '';
    const budget = this.querySelector('[data-product-budget]');
    if (budget) { budget.value = ''; budget.setCustomValidity(''); }
    this.resultsRoot.hidden = true;
    this.questionRoot.hidden = false;
    this.resetButton.hidden = true;
    this.renderQuestion({ focus: true, direction: -1 });
    this.liveStatus.textContent = 'The security brief has been cleared.';
  }

  renderBrief() {
    const selected = questionPath(this.answers)
      .filter((question) => this.answers[question.key])
      .map(
        (question) =>
          `<button type="button" data-edit-key="${question.key}" title="Change ${this.escapeAttr(
            question.title
          )}">${this.escape(
            // The answered question's own wording (alarms say "Not yet, or not sure", cameras "Not yet: ...").
            question.options?.find((option) => option.value === this.answers[question.key])?.label ||
              optionLabel(question.key, this.answers[question.key])
          )}<span aria-hidden="true">×</span></button>`
      );
    this.briefRoot.innerHTML = selected.length ? selected.join('') : '<span>Nothing selected yet</span>';
    this.resetButton.hidden = !selected.length;
  }

  quickStartsHtml() {
    const tiles = QUICK_STARTS.map((quick) => ({ quick, count: countModels(this.products, quick.answers) }))
      .filter(({ count }) => count >= MIN_QUICK_START_MODELS)
      .map(({ quick, count }) => `<button type="button" class="cgau-finder__quick-tile" data-quick-start="${quick.id}">
          <strong>${this.escape(quick.label)}</strong>
          <small>${this.escape(quick.help)}</small>
          <small class="cgau-finder__stock" data-stock="some">${this.escape(countLine(count, quick.answers.category))}</small>
        </button>`);
    if (!tiles.length) return '';
    return `<div class="cgau-finder__quick" role="group" aria-labelledby="CgauQuick-${this.dataset.sectionId || 'x'}">
        <p class="cgau-finder__quick-title" id="CgauQuick-${this.dataset.sectionId || 'x'}">Quick starts <span>One tap. Change any answer after.</span></p>
        <div class="cgau-finder__quick-grid">${tiles.join('')}</div>
      </div>`;
  }

  renderQuestion({ focus = false, direction = 1 } = {}) {
    this.stopMotion();
    this.resultVersion++;
    this.catalogueAbort?.abort();
    const questions = questionPath(this.answers);
    this.step = Math.min(this.step, questions.length - 1);
    const question = questions[this.step];
    const selected = this.answers[question.key];
    const percent = Math.round(((this.step + 1) / questions.length) * 100);

    this.progress.setAttribute('role', 'progressbar');
    this.progress.setAttribute('aria-valuemin', '1');
    this.progress.setAttribute('aria-valuemax', String(questions.length));
    this.progress.setAttribute('aria-valuenow', String(this.step + 1));
    this.progress.setAttribute('aria-valuetext', `Step ${this.step + 1} of ${questions.length}`);
    this.progress.innerHTML = `<span style="--cgau-progress:${percent}%"></span>`;

    // v2: every answer shows how many models it leaves, given the answers so far, so nobody
    // walks into a dead end. An answer with none stays tappable: it leads to the sold-out list
    // and the back-in-stock email (board, 21.10.2026).
    const choices = question.options.map((option) => {
      const trial = question.key === 'category' ? { category: option.value } : { ...this.answers, [question.key]: option.value };
      const count = this.products.length ? countModels(this.products, trial) : null;
      const listed = count === 0 ? countModels(this.products, trial, { inStockOnly: false }) : count;
      const stockLine = count === null
        ? ''
        : `<small class="cgau-finder__stock" data-stock="${count ? 'some' : 'none'}">${this.escape(countLine(count, trial.category, listed))}</small>`;
      return `<button type="button" class="cgau-finder__choice${count === 0 ? ' is-empty' : ''}" data-choice="${option.value}" aria-pressed="${
        selected === option.value
      }">
          <span class="cgau-finder__choice-code" aria-hidden="true">${this.escape(option.code)}</span>
          <span><strong>${this.escape(option.label)}</strong><small>${this.escape(option.help)}</small>${stockLine}</span>
          <span class="cgau-finder__choice-arrow" aria-hidden="true">→</span>
        </button>`;
    });

    this.questionRoot.hidden = false;
    this.resultsRoot.hidden = true;
    this.questionRoot.innerHTML = `
      <div class="cgau-finder__question">
        <div class="cgau-finder__question-main">
          ${this.step === 0 ? this.quickStartsHtml() : ''}
          <div class="cgau-finder__step-line">
            <span>Step ${this.step + 1} of ${questions.length}</span>
            <span>One tap advances</span>
          </div>
          <span class="cgau-finder__question-kicker">${this.escape(question.kicker)}</span>
          <h3 data-question-heading tabindex="-1">${this.escape(question.title)}</h3>
          <p class="cgau-finder__question-intro">${this.escape(question.intro)}</p>
          <div class="cgau-finder__choices">${choices.join('')}</div>
        </div>

        <aside class="cgau-finder__insight">
          <div><span>Why we ask</span><p>${this.escape(question.why)}</p></div>
          <div><span>Good to know</span><p>${this.escape(question.tip)}</p></div>
        </aside>

        <div class="cgau-finder__nav">
          ${this.step ? '<button type="button" data-back><span aria-hidden="true">←</span> Back</button>' : '<span></span>'}
          <p>Choose one answer. The next step opens automatically.</p>
        </div>
      </div>`;
    this.renderBrief();
    this.updateSticky();
    if (this.step === questions.length - 1) this.prefetchCatalogue();
    if (focus) requestAnimationFrame(() => {
      if (!this.isConnected || this.questionRoot.hidden) return;
      const heading = this.questionRoot.querySelector('[data-question-heading]');
      heading?.focus({ preventScroll: true });
      this.reveal(heading);
      this.settle(this.questionRoot, direction);
    });
  }

  updateSticky() {
    if (!this.sticky) return;
    const show = !this.questionRoot.hidden && this.canSkip();
    this.sticky.hidden = !show;
    if (!show) return;
    this.liftSticky();
    const count = countModels(this.products, this.answers);
    const hasClose = !count && closeMatches(this.products, withOpenDefaults(this.answers), { limit: 1 }).length > 0;
    this.sticky.querySelector('button').textContent = count
      ? `Show my ${count} ${nounFor(this.answers.category, count)}`
      : hasClose ? 'See close matches' : 'See why nothing fits';
  }

  // The theme's compare tray (fixed, z-index 40, 122 px tall on a 390 px phone) covered the pinned
  // bar once a product was shortlisted (storefront-qa, 21.10.2026). The bar now sits above the tray
  // whenever it is open, measured rather than guessed, and follows it as it opens, closes or resizes.
  liftSticky() {
    if (!this.sticky) return;
    const tray = document.querySelector('[data-compare-tray]');
    if (tray && !this.trayWatch && typeof MutationObserver !== 'undefined') {
      this.trayWatch = new MutationObserver(() => this.liftSticky());
      this.trayWatch.observe(tray, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
      this.onTrayResize = () => this.liftSticky();
      window.addEventListener('resize', this.onTrayResize, { passive: true });
    }
    const rect = tray && !tray.hidden ? tray.getBoundingClientRect() : null;
    const lift = rect && rect.height ? Math.max(0, Math.ceil(window.innerHeight - rect.top)) : 0;
    this.sticky.style.setProperty('--cgau-sticky-lift', `${lift}px`);
  }

  // 21.10.2026: focus() alone scrolled a heading to the very top of the window, under the
  // sticky header (65 px on phones), whenever the tapped answer sat low on the screen. The
  // heading now lands below the header (its scroll-margin-top), and only moves when needed.
  reveal(element) {
    if (!element) return;
    const top = element.getBoundingClientRect().top;
    const margin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
    if (top >= margin && top <= innerHeight * 0.5) return;
    try {
      element.scrollIntoView({ block: 'start', behavior: 'instant' });
    } catch {
      element.scrollIntoView(true);
    }
  }

  async renderResults() {
    this.questionRoot.hidden = true;
    this.resultsRoot.hidden = false;
    if (this.sticky) this.sticky.hidden = true;
    this.resultsRoot.innerHTML = `
      <div class="cgau-finder__loading" role="status">
        <span aria-hidden="true"></span>
        <strong>Checking the brief</strong>
        <small>Compatibility → features → condition → live stock</small>
      </div>`;

    if (!this.readBudget()) { this.resultsRoot.hidden = true; this.questionRoot.hidden = false; return; }
    this.catalogueAbort?.abort();
    const controller = new AbortController();
    this.catalogueAbort = controller;
    const version = ++this.resultVersion;
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    const prefetch = this.prefetch && Date.now() - this.prefetch.at < PREFETCH_MAX_AGE ? this.prefetch : null;
    this.prefetch = null;
    try {
      const fresh = await (prefetch
        ? prefetch.promise.catch(() => this.refreshCatalogue(controller.signal))
        : this.refreshCatalogue(controller.signal));
      this.products = normaliseCatalogue(fresh);
    } catch {
      if (version === this.resultVersion && this.isConnected) this.renderCatalogueError();
      return;
    } finally { window.clearTimeout(timeout); }
    if (version !== this.resultVersion || !this.isConnected) return;

    if (!this.products.length) {
      this.renderCatalogueError();
      return;
    }
    this.visibleCount = RESULTS_PAGE;
    this.paintResults({ announce: true });
  }

  computeResults() {
    const exact = matchProducts(this.products, this.answers, { limit: this.products.length });
    const groups = groupByModel(exact);
    const close = groups.length < CLOSE_MATCH_BELOW
      ? groupByModel(closeMatches(this.products, this.answers, {
        limit: 12,
        exclude: exact.map((item) => item.product.id),
        excludeModels: groups.map((group) => group.key),
      })).slice(0, 3)
      : [];
    return { groups, close };
  }

  // v2: condition and setup-price chips. Pick any mix of conditions; counts are models.
  filtersHtml() {
    const picked = picks(this.answers, 'condition');
    const active = (value) => !picked.length || picked.includes('any') || picked.includes(value);
    let anyCount = 0;
    const conditionChips = CONDITION_FILTERS.map((value) => {
      const count = countModels(this.products, { ...this.answers, condition: [value] });
      anyCount += count;
      return `<button type="button" class="cgau-finder__chip" data-filter-condition="${value}" aria-pressed="${active(value)}" aria-label="${this.escapeAttr(
        `${optionLabel('condition', value)} ${count} ${nounFor(this.answers.category, count)}`
      )}">${this.escape(
        optionLabel('condition', value)
      )} <span>${count}</span></button>`;
    }).join('');
    const budgetNow = knownPrice(this.answers.budgetCents) ? this.answers.budgetCents : null;
    const budgetChips = [null, ...BUDGET_STEPS].map((cents) => {
      const label = cents === null ? 'Any price' : `Under ${money(cents).replace(/\.00$/, '')}`;
      const count = countModels(this.products, { ...this.answers, budgetCents: cents });
      anyCount += count;
      return `<button type="button" class="cgau-finder__chip" data-filter-budget="${cents ?? ''}" aria-pressed="${budgetNow === cents}" aria-label="${this.escapeAttr(
        `${label} ${count} ${nounFor(this.answers.category, count)}, setup price`
      )}">${this.escape(
        label
      )} <span>${count}</span></button>`;
    }).join('');
    // With no filter set and every chip at 0, no condition or price change can produce a match, so
    // the chips are noise. A set filter keeps its chips, so the shopper can always undo it.
    const conditionFiltered = picked.length > 0 && !picked.includes('any') && !CONDITION_FILTERS.every((value) => picked.includes(value));
    if (!anyCount && !conditionFiltered && budgetNow === null) return '';
    return `<div class="cgau-finder__filters">
        <div class="cgau-finder__filter-row" role="group" aria-label="Condition"><span>Condition</span>${conditionChips}</div>
        <div class="cgau-finder__filter-row" role="group" aria-label="Setup price"><span>Setup price</span>${budgetChips}</div>
      </div>`;
  }

  toggleCondition(value) {
    const current = picks(this.answers, 'condition').filter((item) => CONDITION_FILTERS.includes(item));
    const active = new Set(current.length ? current : CONDITION_FILTERS);
    if (active.has(value)) {
      if (active.size === 1) { this.liveStatus.textContent = 'At least one condition stays selected.'; return; }
      active.delete(value);
    } else active.add(value);
    this.answers.condition = active.size === CONDITION_FILTERS.length ? undefined : [...active];
    this.visibleCount = RESULTS_PAGE;
    this.paintResults();
    this.refocus(`[data-filter-condition="${value}"]`);
    this.liveStatus.textContent = `Condition filter: ${optionLabel('condition', [...active])}. ${this.resultsRoot.querySelector('h3')?.textContent || ''}`.trim();
  }

  setBudget(value) {
    const cents = value === '' ? null : Number(value);
    this.answers.budgetCents = Number.isSafeInteger(cents) ? cents : null;
    this.visibleCount = RESULTS_PAGE;
    this.paintResults();
    this.refocus(`[data-filter-budget="${value}"]`);
    this.liveStatus.textContent = `${cents ? `Setup price under ${money(cents)}.` : 'Any setup price.'} ${this.resultsRoot.querySelector('h3')?.textContent || ''}`.trim();
  }

  // A chip tap redraws the results; focus goes back to the same chip (or the results heading),
  // not to the top of the page, so keyboard and screen-reader users keep their place.
  refocus(selector) {
    const target = this.resultsRoot.querySelector(selector);
    if (target) target.focus({ preventScroll: true });
    else this.focusResultHeading();
  }

  chooseOption(key, id) {
    this.choice.set(String(key), String(id));
    const card = this.resultsRoot.querySelector(`[data-model-key="${CSS.escape(String(key))}"]`);
    this.repaintCard(card);
  }

  repaintCard(fromElement) {
    const card = fromElement?.closest?.('[data-model-key]');
    if (!card) return;
    const entry = this.groupsByKey.get(card.dataset.modelKey);
    if (!entry) return;
    const focusedId = document.activeElement?.dataset?.id;
    const focusedKind = ['modelOption', 'hubSelect', 'cardTick'].find((kind) => document.activeElement?.dataset?.[kind] !== undefined);
    card.outerHTML = this.groupCard(entry.group, { close: entry.close });
    if (focusedKind && focusedId) {
      const attr = { modelOption: 'data-model-option', hubSelect: 'data-hub-select', cardTick: 'data-card-tick' }[focusedKind];
      this.resultsRoot.querySelector(`[data-model-key="${CSS.escape(entry.group.key)}"] [${attr}][data-id="${CSS.escape(focusedId)}"]`)?.focus();
    }
  }

  paintResults({ announce = false } = {}) {
    const { groups, close } = this.computeResults();
    this.groupsByKey = new Map([
      ...groups.map((group) => [group.key, { group, close: false }]),
      ...close.map((group) => [group.key, { group, close: true }]),
    ]);
    if (!groups.length) {
      this.renderNoMatch(close, { announce });
      return;
    }
    const visible = groups.slice(0, this.visibleCount);
    const remaining = groups.length - visible.length;
    const noun = nounFor(this.answers.category, groups.length);
    const countLabel = `${groups.length} ${noun} ${groups.length === 1 ? 'matches' : 'match'} all your answers`;
    const explanation = groups.length > RESULTS_PAGE
      ? `Showing ${visible.length} of ${groups.length}, ${ranksByPrice(this.answers) ? 'cheapest first' : 'best fit for what matters most first, then price'}.`
      : `${groups.length === 1 ? 'It fits' : 'Each one fits'} every answer and is in stock. Change the condition or price below at any time.`;
    this.resultsRoot.innerHTML = `
      <div class="cgau-finder__results-head">
        <div>
          <span class="cgau-finder__result-status"><i aria-hidden="true"></i> Exact-match check passed</span>
          <h3>${this.escape(countLabel)}</h3>
          <p>${this.escape(explanation)}</p>
        </div>
        <button type="button" class="cgau-finder__restart" data-edit-answers>Edit answers</button>
      </div>
      ${this.filtersHtml()}
      <div class="cgau-finder__results cgau-finder__results--${Math.min(visible.length, RESULTS_PAGE)}">
        ${visible.map((group) => this.groupCard(group)).join('')}
      </div>
      ${remaining > 0
        ? `<div class="cgau-finder__more"><button type="button" data-show-more>Show ${Math.min(remaining, RESULTS_PAGE)} more</button></div>`
        : ''}
      ${this.closeBlockHtml(close)}
      ${this.footHtml()}`;
    if (announce) {
      emitFinderOutcome('finder_completed', groups.length);
      this.liveStatus.textContent = countLabel;
      this.focusResultHeading();
    }
  }

  closeBlockHtml(close) {
    if (!close.length) return '';
    return `<section class="cgau-finder__close" aria-label="Close, but not exact">
        <h4>Close, but not exact</h4>
        <p>Each one misses one thing you picked, named on the card. Everything else you picked fits.</p>
        <div class="cgau-finder__results cgau-finder__results--${Math.min(close.length, RESULTS_PAGE)}">${close.map((group) => this.groupCard(group, { close: true })).join('')}</div>
      </section>`;
  }

  footHtml() {
    return `<div class="cgau-finder__result-foot">
        <p><strong>Prices and stock were checked when these results opened</strong>, and adding to cart checks them again. A setup total adds up items sold separately; there is no bundle discount. No monthly fee is needed to record: footage saves on the camera, a microSD card or a HomeBase.</p>
        <p>Every grade has our 12-month warranty. <a href="/pages/condition-grading-guide">What each grade means</a> · <a href="/pages/camguard-australia-warranty-policy">Warranty policy</a></p>
        <a href="/pages/eufy-compatibility">Open the compatibility checker <span aria-hidden="true">→</span></a>
      </div>`;
  }

  // 21.10.2026: results stopped at six cards, so matches seven onwards (14 of the 20 outdoor
  // matches for an "I do not know yet" brief) could never be seen.
  showMore() {
    const before = this.visibleCount;
    this.visibleCount += RESULTS_PAGE;
    this.paintResults();
    // The first results grid is the exact matches; close matches sit in their own section.
    const cards = this.resultsRoot.querySelector('.cgau-finder__results')?.querySelectorAll(':scope > .cgau-finder-card') || [];
    this.liveStatus.textContent = `Showing ${Math.min(this.visibleCount, cards.length)} more models.`;
    const link = cards[before]?.querySelector('h4 a');
    link?.focus({ preventScroll: true });
    this.reveal(cards[before]);
  }

  renderNoMatch(close = [], { announce = true } = {}) {
    const advice = noMatchAdvice(this.products, this.answers);
    // 21.10.2026: sold-out matches link to their product pages, where the back-in-stock email
    // sign-up lives; as plain text they were a dead end (all 525 doorbell briefs ended here).
    const unavailable = advice.unavailable.length
      ? `<div class="cgau-finder__unavailable">
          <span>Matching ${advice.unavailable.length === 1 ? 'product' : 'products'}, sold out right now</span>
          ${advice.unavailable
            .map(
              (item) =>
                `<p><a href="${this.escapeAttr(item.product.url || `/products/${item.product.handle}`)}">${this.escape(
                  item.product.title
                )}</a> <strong>Sold out</strong></p>`
            )
            .join('')}
        </div>`
      : '';

    this.resultsRoot.innerHTML = `
      <div class="cgau-finder__no-match">
        <div class="cgau-finder__no-match-head">
          <span class="cgau-finder__stop" aria-hidden="true">0</span>
          <div><span class="cgau-finder__result-status">Honest result</span><h3>${this.escape(advice.headline)}</h3></div>
        </div>
        <p class="cgau-finder__no-match-lead">We never call an almost-match a match.${close.length ? ' The close matches below each name the one thing they miss.' : ''}</p>
        <div class="cgau-finder__no-match-grid">
          <div><span>What you asked for</span><p>${this.escape(advice.need)}</p></div>
          <div><span>Why the result is empty</span><p>${this.escape(advice.reason)}</p></div>
          <div><span>What you actually need</span><p>${this.escape(advice.next)}</p></div>
        </div>
        ${this.filtersHtml()}
        ${unavailable}
        ${this.closeBlockHtml(close)}
        <div class="cgau-finder__no-match-actions">
          <button type="button" class="cgau-finder__restart" data-edit-answers>Edit one answer</button>
          <a href="/pages/eufy-compatibility">Check an exact model</a>
          <a href="/pages/contact">Ask CamGuard</a>
        </div>
      </div>`;
    if (announce) {
      emitFinderOutcome('finder_no_results', 0);
      this.liveStatus.textContent = advice.headline;
      this.focusResultHeading();
    }
  }

  renderCatalogueError() {
    this.resultsRoot.innerHTML = `
      <div class="cgau-finder__no-match">
        <div class="cgau-finder__no-match-head">
          <span class="cgau-finder__stop" aria-hidden="true">!</span>
          <div><span class="cgau-finder__result-status">Catalogue check unavailable</span><h3>We cannot verify stock safely right now.</h3></div>
        </div>
        <p class="cgau-finder__no-match-lead">No recommendations have been shown because the current catalogue data did not load. Refresh the page or ask CamGuard for a manual match.</p>
        <div class="cgau-finder__no-match-actions"><button type="button" class="cgau-finder__restart" data-restart>Try again</button><a href="/pages/contact">Ask CamGuard</a></div>
      </div>`;
    this.liveStatus.textContent = 'Catalogue data could not be verified, so no recommendations were shown.';
    this.focusResultHeading();
  }

  focusResultHeading() {
    requestAnimationFrame(() => {
      const heading = this.resultsRoot.querySelector('h3');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      this.reveal(heading);
      this.settle(this.resultsRoot.querySelector('.cgau-finder__results-head, .cgau-finder__no-match'));
      this.resultsRoot.querySelectorAll('.cgau-finder-card').forEach((card, index) => this.settle(card, 0, Math.min(index, 2) * 45));
    });
  }

  // v2 setup box: what the product needs (a HomeBase, ticked in) or can use (a microSD card,
  // unticked), each a separately sold in-stock listing, with the total bought separately and
  // one button that adds them all after a live stock and price check. The box never says
  // "bundle", "kit" or "save" (compliance seat, 21.10.2026); the separate "Build my kit from
  // this" link is the Bundle Builder's own name for the shopper's chosen set (its contract).
  setupHtml(product, failure = '') {
    // A close match's setup is built with its one named miss set aside (relaxedAnswers).
    const setup = setupFor(this.products, product, failure ? relaxedAnswers(this.answers, failure) : this.answers);
    const hubLine = setup.lines.find((line) => line.role === 'hub');
    const cardLine = setup.lines.find((line) => line.role === 'card');
    if (!hubLine && !cardLine && !setup.missing) return { html: '', ids: [], items: [product] };
    const id = String(product.id);
    const hubPick = hubLine
      ? setup.hubChoices.find((hub) => String(hub.id) === this.hubChoice.get(id)) || hubLine.product
      : null;
    const cardOn = Boolean(cardLine) && this.cardTicked.has(id);
    const ids = [id, ...(hubPick ? [String(hubPick.id)] : []), ...(cardOn ? [String(cardLine.product.id)] : [])];
    const total = product.price + (hubPick ? hubPick.price : 0) + (cardOn ? cardLine.product.price : 0);
    const hubKeyName = hubPick ? hubKey(hubPick) : '';
    const hubSelect = hubPick && setup.hubChoices.length > 1
      ? `<label class="cgau-finder-setup__pick">Choose the HomeBase
          <select data-hub-select data-id="${this.escapeAttr(id)}" aria-label="Choose the HomeBase">${setup.hubChoices
            .map((hub) => `<option value="${this.escapeAttr(String(hub.id))}"${String(hub.id) === String(hubPick.id) ? ' selected' : ''}>${this.escape(
              `${hubName(hub.title)}, ${shortGrade(hub.condition)}, ${money(hub.price, hub.currency)}`
            )}</option>`)
            .join('')}</select></label>`
      : '';
    const conditionNote = hubPick && !setup.hubConditionMet
      ? `<p class="cgau-finder-setup__note">No HomeBase in your chosen condition is in stock; this one is ${this.escape(shortGrade(hubPick.condition))}.</p>`
      : '';
    const rows = [
      `<li><span>This ${this.escape(nounFor(this.answers.category, 1))}</span><strong>${money(product.price, product.currency)}</strong></li>`,
      hubPick
        ? `<li><span><a href="${this.escapeAttr(hubPick.url || `/products/${hubPick.handle}`)}">${this.escape(hubName(hubPick.title))}</a> <em>needed</em><small>${this.escape(
            shortGrade(hubPick.condition)
          )}. Pairs with ${hubReach(this.products, hubKeyName, { inStockOnly: true })} models in stock now</small></span><strong>${money(hubPick.price, hubPick.currency)}</strong></li>`
        : '',
      cardLine
        ? `<li><label><input type="checkbox" data-card-tick data-id="${this.escapeAttr(id)}" aria-label="${this.escapeAttr(
            `Add ${modelName(cardLine.product.title)}, ${money(cardLine.product.price, cardLine.product.currency)}, to this setup`
          )}"${cardOn ? ' checked' : ''}> <span><a href="${this.escapeAttr(
            cardLine.product.url || `/products/${cardLine.product.handle}`
          )}">${this.escape(modelName(cardLine.product.title))}</a> <em>stores the recordings; skip it if you have one</em></span></label><strong>${money(
            cardLine.product.price,
            cardLine.product.currency
          )}</strong></li>`
        : '',
    ].join('');
    const html = `<div class="cgau-finder-setup">
        <p class="cgau-finder-setup__title">${hubPick || setup.missing ? 'What it needs to work' : 'To save recordings'}</p>
        ${setup.missing ? `<p class="cgau-finder-setup__missing">${this.escape(setup.missing)}</p>` : ''}
        ${hubPick || cardLine ? `<ul>${rows}</ul>${hubSelect}${conditionNote}` : ''}
        ${ids.length > 1
          ? `<p class="cgau-finder-setup__total"><span>Total, bought separately</span><strong>${money(total, product.currency)}</strong></p>
             <button type="button" class="cgau-finder-setup__add" data-add-setup="${this.escapeAttr(ids.join(','))}" aria-describedby="cgau-card-title-${this.escapeAttr(id)}">Add these ${ids.length} to cart</button>`
          : ''}
      </div>`;
    return { html, ids, items: [product, ...(hubPick ? [hubPick] : []), ...(cardOn ? [cardLine.product] : [])] };
  }

  groupCard(group, { close = false } = {}) {
    const chosenId = this.choice.get(group.key);
    const item = group.items.find((entry) => String(entry.product.id) === String(chosenId)) || group.items[0];
    const product = item.product;
    const url = product.url || `/products/${product.handle}`;
    const image = product.image
      ? `<img loading="lazy" decoding="async" src="${this.escapeAttr(product.image)}" alt="${this.escapeAttr(product.title)}">`
      : '<span class="cgau-finder-card__placeholder" aria-hidden="true">CG</span>';
    const options = group.items.length > 1
      ? `<div class="cgau-finder-card__options" role="group" aria-label="Condition and colour">${group.items
          .map(({ product: option }) => {
            const colour = colourOf(option.title);
            const label = `${colour ? `${colour}, ` : ''}${shortGrade(option.condition)}, ${money(option.price, option.currency)}`;
            return `<button type="button" data-model-option data-model-key="${this.escapeAttr(group.key)}" data-id="${this.escapeAttr(
              String(option.id)
            )}" aria-pressed="${String(option.id) === String(product.id)}">${this.escape(label)}</button>`;
          })
          .join('')}</div>`
      : '';
    const reasons = item.reasons.filter((reason) => reason !== product.condition);
    const why = item.guidance?.why
      ? `<p class="cgau-finder-card__note"><strong>Why it fits:</strong> ${this.escape(item.guidance.why)}</p>`
      : '';
    const tradeoff = item.guidance?.tradeoff
      ? `<p class="cgau-finder-card__note"><strong>Worth knowing:</strong> ${this.escape(item.guidance.tradeoff)}</p>`
      : '';
    const setup = this.setupHtml(product, close ? item.failures?.[0] : '');
    const kitHref = builderHref(setup.items, this.answers);
    // Card buttons keep their visible words as their name; the card title describes them
    // ("Add to cart, eufyCam C35"), so a screen reader knows which product each button is for.
    const titleId = `cgau-card-title-${this.escapeAttr(String(product.id))}`;
    const note = product.compatibilityNote
      ? `<details class="cgau-finder-card__details"><summary>Compatibility details</summary><p>${this.escape(
          product.compatibilityNote
        ).replace(/\n+/g, '<br>')}</p></details>`
      : '';
    const fit = close
      ? `<p class="cgau-finder-card__miss">Close match: ${this.escape(item.miss || 'misses one of your answers')}</p>`
      : '<p class="cgau-finder-card__fit">Matches all your answers</p>';

    return `<article class="cgau-finder-card${close ? ' cgau-finder-card--close' : ''}" data-model-key="${this.escapeAttr(group.key)}">
      <a class="cgau-finder-card__image" href="${this.escapeAttr(url)}">${image}</a>
      <div class="cgau-finder-card__body">
        <div class="cgau-finder-card__topline"><span class="cgau-finder-card__grade">${this.escape(shortGrade(product.condition))}</span><strong>${product.priceVaries ? 'From ' : ''}${money(
          product.price, product.currency
        )}</strong></div>
        ${fit}
        <h4 id="${titleId}"><a href="${this.escapeAttr(url)}">${this.escape(modelName(product.title))}</a></h4>
        ${options}
        ${reasons.length ? `<div class="cgau-finder-card__reasons">${reasons.map((reason) => `<span>${this.escape(reason)}</span>`).join('')}</div>` : ''}
        ${why}${tradeoff}
        ${setup.html}
        ${note}
        <div class="cgau-finder-card__actions">
          <button type="button" class="cgau-finder-card__add" data-add-one="${this.escapeAttr(String(product.id))}" aria-describedby="${titleId}">${setup.ids.length > 1 ? `Add the ${this.escape(nounFor(this.answers.category, 1))} only` : 'Add to cart'}</button>
          <button type="button" class="cgau-compare-toggle" data-cgau-compare data-id="${this.escapeAttr(String(product.id))}" data-handle="${this.escapeAttr(product.handle)}" data-title="${this.escapeAttr(product.title)}" data-url="${this.escapeAttr(url)}" aria-pressed="false" aria-describedby="${titleId}">Compare</button>
        </div>
        ${kitHref ? `<a class="cgau-finder__build" data-cgb-handoff href="${this.escapeAttr(kitHref)}">Build my kit from this <span aria-hidden="true">→</span></a>` : ''}
        <a class="cgau-finder-card__cta" href="${this.escapeAttr(url)}">See the exact product <span aria-hidden="true">→</span></a>
        <p class="cgau-finder-card__status" role="status" aria-live="polite" data-card-status></p>
      </div>
    </article>`;
  }

  // v2: add one listing or a whole setup. Each listing's live price and stock are checked first
  // (the same pattern as the Setup Desk at /pages/bundles); if anything changed, nothing is added.
  async addListings(button, ids) {
    const status = button.closest('.cgau-finder-card')?.querySelector('[data-card-status]');
    const say = (text) => { if (status) status.textContent = text; };
    if (this.adding) return;
    const listings = ids.map((id) => this.products.find((product) => String(product.id) === String(id))).filter(Boolean);
    if (!listings.length || listings.length !== ids.length) { say('That item is no longer listed. Refresh the results.'); return; }
    this.adding = true;
    button.disabled = true;
    say('Checking stock and price…');
    try {
      const items = [];
      for (const listing of listings) {
        const response = await fetch(`${storeRoot()}products/${encodeURIComponent(listing.handle)}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('check');
        const fresh = await response.json();
        const variant = asArray(fresh.variants).find((entry) => entry.available && entry.price === listing.price);
        if (!variant) { say('A price or stock level just changed, so nothing was added. Refresh the results to see the latest.'); return; }
        items.push({ id: variant.id, quantity: 1 });
      }
      const response = await fetch(`${storeRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) { say('Shopify could not add these items. Check your cart before trying again.'); return; }
      say(items.length > 1 ? `Added ${items.length} items. Opening your cart…` : 'Added. Opening your cart…');
      location.assign(`${storeRoot()}cart`);
    } catch {
      say('The stock check failed, so nothing was added. Please try again.');
    } finally {
      this.adding = false;
      button.disabled = false;
    }
  }

  escape(value) {
    const node = document.createElement('span');
    node.textContent = value || '';
    return node.innerHTML;
  }

  escapeAttr(value) {
    return this.escape(value).replace(/"/g, '&quot;');
  }
}

export {
  knownPrice, money, budgetCents,
  CATEGORY_QUESTION,
  OPEN_DEFAULTS,
  SOFT_GATES,
  picks,
  withOpenDefaults,
  countInStock,
  closeMatches,
  continuousMissIsPowerOnly,
  builderHref,
  builderHub,
  relaxedAnswers,
  countModels,
  countLine,
  setupFor,
  hubReach,
  hubOptions,
  normaliseCatalogue,
  wishFact,
  CONDITION_FILTERS,
  hubKey,
  maxCardGb,
  questionPath,
  categoryMatches,
  categoryStockLine,
  isHubProduct,
  setupFact,
  ranksByPrice,
  isWirelessS4,
  effectiveCapabilities,
  systemMatches,
  powerMatches,
  priorityMatches,
  accessoryNeedMatches,
  conditionMatches,
  evaluateProduct,
  matchProducts,
  matchGuidance,
  needSentence,
  closestFailureSummary,
  noMatchAdvice,
};

globalThis.CGAUFinderEngine = Object.freeze({
  questionPath,
  evaluateProduct,
  matchProducts,
  needSentence,
  noMatchAdvice,
});

if (typeof customElements !== 'undefined' && !customElements.get('cgau-camera-finder')) {
  customElements.define('cgau-camera-finder', CgauCameraFinder);
}
